import type { CSSProperties, FormEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Pencil, Trash2, Folder, FolderPlus, ChevronRight, ChevronLeft,
  LayoutGrid, Rows3, List, CalendarDays, GanttChartSquare, FolderKanban,
  CheckCircle2, Circle, ChevronDown, MoreHorizontal, Flag, Undo2, User,
  Download, Upload, Camera, Search, X, AlertTriangle, Clock, Loader2,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, rectSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, ApiError } from "@/lib/api";
import { fmtDate, todayVN, addDaysISO, parseVNDateToISO } from "@/lib/utils";
import { csvEscape, parseCsv } from "../../shared/csv/csvCore";
import {
  // types
  type Task, type TaskSection, type CustomField, type BoardColumn, type BoardColumnGroup,
  type Project, type GroupKind, type TaskProjectGroup, type FieldType,
  type Customer, type Page, type InventoryItem, type ChecklistItem, type PhotoChecklistDefault,
  // constants
  TASK_PROJECT_GROUPS, CHECKLIST_KEY, RENTAL_ITEMS_KEY, TASK_STATUS_LABEL,
  // utils
  deriveGroupKind, getProjectStream, getErrorMessage, extractConflictCandidates,
  getFieldHistory, pushFieldHistory, confirmAsync, parseAmount, useCurrentStream,
  // components
  Input, DateInputVN, Textarea, Select, PrimaryBtn, GhostBtn, DangerBtn, ConfirmBtn,
  Modal, ErrorBox, AlbumCell, SuggestInput,
  CustomerSearchPicker, InventoryMultiPicker, ProductMultiPicker,
  TaskPiwigoActions, ChecklistEditor, CustomerContactIcons,
} from "../../AppStatic";

/* ============================================================ Tasks
 * K9 Phase 3 — Port hệ Sections + Custom Fields từ admin.tasks.tsx (Supabase) sang PHP API.
 * Endpoints: /tasks/sections, /tasks/fields. Field/markup giữ nguyên label tiếng Việt cũ.
 */

const PRIORITY_LABEL: Record<string, string> = { low: "Thấp", med: "Vừa", high: "Cao", urgent: "Khẩn", normal: "Bình thường" };

function capitalizeFirstVN(s: string | null | undefined): string {
  const t = (s ?? "").replace(/^\s+/, "");
  if (!t) return s ?? "";
  return t.charAt(0).toLocaleUpperCase("vi-VN") + t.slice(1);
}
const STATUS_LABEL: Record<string, string> = {
  todo: "Chưa làm", in_progress: "Đang làm", done: "Đã xong", blocked: "Tạm dừng",
  not_sent: "Chưa gửi", sent: "Đã gửi", returned: "Đã nhận về", notes: "Ghi chú",
};
function isDoneStatusFor(section: TaskSection | null | undefined, status: string): boolean {
  if (!section?.board_columns?.length) return status === "done";
  const c = section.board_columns.find((x) => x.key === status);
  return c?.group === "done";
}

/**
 * sanitizeTaskPayload — guard cho mọi request ghi /tasks.
 * - Bỏ field có giá trị `undefined` (tránh ghi đè rỗng ngoài ý muốn).
 * - Nếu payload có `custom_values`, ép về object thuần (không cho null/undefined
 *   biến thành "{}" mất key cũ; caller phải merge trước khi gọi).
 * - Không tự xoá các field ngày: caller chịu trách nhiệm chỉ truyền date
 *   khi user thật sự sửa trong form (start_date / due_date / return_date /
 *   shoot_time / custom_values.rental_date).
 */
function sanitizeTaskPayload<T extends Record<string, unknown>>(body: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (typeof v === "undefined") continue;
    out[k] = v;
  }
  if ("custom_values" in out) {
    const cv = out.custom_values;
    if (cv == null || typeof cv !== "object" || Array.isArray(cv)) {
      delete out.custom_values; // không gửi custom_values rỗng/sai kiểu → backend giữ nguyên
    }
  }
  return out as T;
}

function useSections(projectId: string | null) {
  return useQuery({
    queryKey: ["task_sections", projectId ?? "none"],
    enabled: !!projectId,
    queryFn: () => api.get<TaskSection[]>("/tasks/sections", { project_id: projectId! }),
  });
}
function useCustomFields(projectId: string | null) {
  return useQuery({
    queryKey: ["task_custom_fields", projectId ?? "none"],
    enabled: !!projectId,
    queryFn: () => api.get<CustomField[]>("/tasks/fields", { project_id: projectId! }),
  });
}

/* ----- SectionsDialog: thêm/đổi tên/xoá/sắp xếp section + cấu hình board_columns ----- */
function SectionsDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const sections = useSections(projectId);
  const [newName, setNewName] = useState("");
  const inv = () => qc.invalidateQueries({ queryKey: ["task_sections", projectId] });
  const create = useMutation({
    mutationFn: (name: string) => api.post("/tasks/sections", { project_id: projectId, name, sort_order: (sections.data?.length ?? 0) }),
    onSuccess: () => { setNewName(""); inv(); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<TaskSection> }) =>
      api.put(`/tasks/sections/${encodeURIComponent(id)}`, patch),
    onSuccess: () => inv(),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/tasks/sections/${encodeURIComponent(id)}`),
    onSuccess: () => { inv(); qc.invalidateQueries({ queryKey: ["tasks", projectId] }); },
  });
  return (
    <Modal title="Quản lý sections" onClose={onClose}>
      <div className="space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create.mutate(newName.trim()); }} className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Tên section mới" />
          <PrimaryBtn type="submit" disabled={create.isPending}>+ Thêm</PrimaryBtn>
        </form>
        <div className="space-y-2 max-h-[50vh] overflow-auto">
          {(sections.data ?? []).map((s, idx) => (
            <SectionRow key={s.id} section={s} index={idx} total={sections.data?.length ?? 0}
              onRename={(name) => update.mutate({ id: s.id, patch: { name } })}
              onReorder={(dir) => {
                const arr = [...(sections.data ?? [])];
                const j = idx + dir;
                if (j < 0 || j >= arr.length) return;
                update.mutate({ id: s.id, patch: { sort_order: j } });
                update.mutate({ id: arr[j].id, patch: { sort_order: idx } });
              }}
              onBoardChange={(cols) => update.mutate({ id: s.id, patch: { board_columns: cols } })}
              onDelete={async () => { if (await confirmAsync(`Xoá section "${s.name}"?\nTask trong section sẽ bị bỏ phân nhóm.`)) del.mutate(s.id); }}
            />
          ))}
          {(sections.data?.length ?? 0) === 0 && <p className="text-sm italic text-muted-foreground">Chưa có section.</p>}
        </div>
        <div className="flex justify-end">
          <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
        </div>
      </div>
    </Modal>
  );
}

function SectionRow({ section, index, total, onRename, onReorder, onBoardChange, onDelete }: {
  section: TaskSection; index: number; total: number;
  onRename: (name: string) => void; onReorder: (dir: -1 | 1) => void;
  onBoardChange: (cols: BoardColumn[]) => void; onDelete: () => void;
}) {
  const [name, setName] = useState(section.name);
  const [showBoard, setShowBoard] = useState(false);
  const cols = section.board_columns ?? [];
  return (
    <div className="rounded border border-border bg-card p-2 space-y-2">
      <div className="flex gap-2 items-center">
        <span className="text-[11px] text-muted-foreground tabular-nums w-6 text-right">{index + 1}</span>
        <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => { if (name !== section.name && name.trim()) onRename(name.trim()); }} className="flex-1" />
        <GhostBtn type="button" onClick={() => onReorder(-1)} disabled={index === 0}>↑</GhostBtn>
        <GhostBtn type="button" onClick={() => onReorder(1)} disabled={index === total - 1}>↓</GhostBtn>
        <GhostBtn type="button" onClick={() => setShowBoard((v) => !v)}>{showBoard ? "Ẩn cột" : `Cột (${cols.length})`}</GhostBtn>
        <DangerBtn type="button" onClick={onDelete}>Xoá</DangerBtn>
      </div>
      {showBoard ? (
        <BoardColumnsEditor cols={cols} onChange={onBoardChange} />
      ) : null}
    </div>
  );
}

const BOARD_PRESETS: { key: string; label: string; cols: BoardColumn[] }[] = [
  { key: "rental", label: "Cho thuê (Chưa gửi → Đã gửi → Đã nhận về)", cols: [
    { key: "pending",   label: "Chưa gửi",     group: "todo"  },
    { key: "sent",      label: "Đã gửi",       group: "doing" },
    { key: "returned",  label: "Đã nhận về",   group: "done"  },
    { key: "note",      label: "Ghi chú",      group: "notes" },
  ]},
  { key: "craft", label: "Làm đồ (Chưa làm → Đang làm → Đã làm)", cols: [
    { key: "todo",        label: "Chưa làm",   group: "todo"  },
    { key: "in_progress", label: "Đang làm",   group: "doing" },
    { key: "qc",          label: "Đang QC",    group: "doing" },
    { key: "done",        label: "Đã làm",     group: "done"  },
    { key: "blocked",     label: "Tạm dừng",   group: "notes" },
  ]},
  { key: "order", label: "Order (Chờ đặt → Về kho TQ → Về VN → Giao)", cols: [
    { key: "waiting",   label: "Chờ đặt",     group: "todo"  },
    { key: "ordered",   label: "Đã đặt",      group: "doing" },
    { key: "cn_wh",     label: "Về kho TQ",   group: "doing" },
    { key: "vn_wh",     label: "Về VN",       group: "doing" },
    { key: "delivered", label: "Đã giao",     group: "done"  },
  ]},
  { key: "asana", label: "Mặc định (Cần làm → Đang làm → Đã xong)", cols: [
    { key: "todo",        label: "Cần làm",    group: "todo"  },
    { key: "in_progress", label: "Đang làm",   group: "doing" },
    { key: "done",        label: "Đã xong",    group: "done"  },
    { key: "blocked",     label: "Tạm dừng",   group: "notes" },
  ]},
  { key: "props_make", label: "Làm props (thiết kế → in → xử lý → xong)", cols: [
    { key: "todo",          label: "Cần làm",        group: "todo"  },
    { key: "designing",     label: "Đang thiết kế",  group: "doing" },
    { key: "printing",      label: "Đang in",        group: "doing" },
    { key: "printed",       label: "Đã in",          group: "doing" },
    { key: "processing",    label: "Đang xử lý",     group: "doing" },
    { key: "wait_reply",    label: "Chờ phản hồi",   group: "doing" },
    { key: "wait_material", label: "Chờ vật liệu",   group: "doing" },
    { key: "done",          label: "Đã xong",        group: "done"  },
  ]},
];

function BoardColumnsEditor({ cols, onChange }: { cols: BoardColumn[]; onChange: (cols: BoardColumn[]) => void }) {
  const [draftKey, setDraftKey] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [draftGroup, setDraftGroup] = useState<BoardColumnGroup>("todo");
  const [preset, setPreset] = useState("");
  return (
    <div className="space-y-1.5 border-t border-border/50 pt-2">
      <div className="flex gap-2 items-center text-xs">
        <span className="text-muted-foreground">Mẫu cột:</span>
        <Select value={preset} onChange={(e) => setPreset(e.target.value)} className="flex-1">
          <option value="">— Chọn mẫu —</option>
          {BOARD_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </Select>
        <GhostBtn type="button" disabled={!preset} onClick={() => {
          const p = BOARD_PRESETS.find((x) => x.key === preset);
          if (p) onChange(p.cols.map((c) => ({ ...c })));
        }}>Áp dụng</GhostBtn>
      </div>
      {cols.map((c, i) => (
        <div key={c.key + i} className="flex gap-2 items-center text-xs">
          <span className="font-mono w-24 truncate">{c.key}</span>
          <Input value={c.label} onChange={(e) => { const next = [...cols]; next[i] = { ...c, label: e.target.value }; onChange(next); }} className="flex-1" />
          <Select value={c.group} onChange={(e) => { const next = [...cols]; next[i] = { ...c, group: e.target.value as BoardColumnGroup }; onChange(next); }} className="w-28">
            <option value="todo">Cần làm (todo)</option><option value="doing">Đang làm (doing)</option><option value="done">Đã xong (done)</option><option value="notes">Ghi chú (notes)</option>
          </Select>
          <DangerBtn type="button" onClick={() => onChange(cols.filter((_, j) => j !== i))}>−</DangerBtn>
        </div>
      ))}
      <div className="flex gap-2 items-center text-xs pt-1">
        <Input value={draftKey} onChange={(e) => setDraftKey(e.target.value.replace(/\s+/g, "_"))} placeholder="key (vd: doing)" className="w-24" />
        <Input value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} placeholder="Nhãn cột" className="flex-1" />
        <Select value={draftGroup} onChange={(e) => setDraftGroup(e.target.value as BoardColumnGroup)} className="w-28">
          <option value="todo">Cần làm (todo)</option><option value="doing">Đang làm (doing)</option><option value="done">Đã xong (done)</option><option value="notes">Ghi chú (notes)</option>
        </Select>
        <GhostBtn type="button" onClick={() => {
          if (!draftKey.trim() || !draftLabel.trim()) return;
          onChange([...cols, { key: draftKey.trim(), label: draftLabel.trim(), group: draftGroup }]);
          setDraftKey(""); setDraftLabel("");
        }}>+</GhostBtn>
      </div>
    </div>
  );
}

/* ----- BoardColumnsDialog: cấu hình cột Board cho từng section ----- */
const VI_LABEL_TO_KEY: Record<string, string> = {
  "đang thiết kế": "designing", "đang in": "printing", "đã in": "printed",
  "đang xử lý": "processing", "chờ phản hồi": "wait_reply", "chờ vật liệu": "wait_material",
  "đang làm": "in_progress", "đang qc": "qc", "tạm dừng": "blocked", "đang sơn": "painting",
  "chưa làm": "todo", "cần làm": "todo", "đã xong": "done", "đã làm": "done",
};
function viSlug(s: string): string {
  const k = s.trim().toLowerCase();
  if (VI_LABEL_TO_KEY[k]) return VI_LABEL_TO_KEY[k];
  const slug = k.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return slug || "col";
}
function uniqKey(base: string, taken: Set<string>): string {
  let k = base, i = 2;
  while (taken.has(k)) { k = `${base}_${i++}`; }
  return k;
}
const PROPS_MIDDLE = ["Đang làm", "Đang thiết kế", "Đang in", "Đã in", "Đang xử lý", "Chờ phản hồi", "Chờ vật liệu"];

function BoardColumnsDialog({ projectId, currentProjectName, preferSectionId, onClose }: {
  projectId: string; currentProjectName: string; preferSectionId: string | null; onClose: () => void;
}) {
  const qc = useQueryClient();
  const sections = useSections(projectId);
  const list = sections.data ?? [];
  const preferId = preferSectionId && preferSectionId !== "__none" && list.find((s) => s.id === preferSectionId)?.id
    ? preferSectionId : "";
  const [selId, setSelId] = useState<string>(preferId || list[0]?.id || "");
  useEffect(() => {
    if (!selId && list[0]) setSelId(list[0].id);
  }, [list, selId]);
  const cur = list.find((s) => s.id === selId) ?? list[0];

  const [middle, setMiddle] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  useEffect(() => {
    const cols = cur?.board_columns ?? [];
    const mid = cols
      .filter((c) => c.key !== "todo" && c.key !== "done" && c.group !== "notes")
      .map((c) => c.label);
    setMiddle(mid);
  }, [cur?.id]);

  const save = useMutation({
    mutationFn: ({ id, board_columns }: { id: string; board_columns: BoardColumn[] }) =>
      api.put(`/tasks/sections/${encodeURIComponent(id)}`, { board_columns }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task_sections", projectId] });
      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
    },
  });

  function build(): BoardColumn[] {
    const taken = new Set<string>(["todo", "done"]);
    const mid: BoardColumn[] = middle
      .map((label) => label.trim())
      .filter((label) => label.length > 0)
      .map((label) => {
        const base = viSlug(label);
        const key = uniqKey(base === "todo" || base === "done" ? `${base}_x` : base, taken);
        taken.add(key);
        return { key, label, group: "doing" as BoardColumnGroup };
      });
    // An toàn dữ liệu: task cũ có status="in_progress". Nếu user không nhập cột "Đang làm",
    // chèn thêm cột in_progress group=doing để các task cũ vẫn hiển thị đúng.
    if (!mid.some((c) => c.key === "in_progress")) {
      mid.unshift({ key: "in_progress", label: "Đang làm", group: "doing" });
    }
    return [
      { key: "todo", label: "Cần làm", group: "todo" },
      ...mid,
      { key: "done", label: "Đã xong", group: "done" },
    ];
  }

  function handleAdd() {
    const v = draft.trim();
    if (!v) return;
    setMiddle((m) => [...m, v]);
    setDraft("");
  }
  function handleSave() {
    if (!cur) return;
    save.mutate({ id: cur.id, board_columns: build() }, { onSuccess: onClose });
  }

  const title = cur ? `Cột Board cho: ${currentProjectName} — ${cur.name}` : `Cột Board cho: ${currentProjectName}`;

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground border-l-2 border-sky-500/60 pl-2 bg-sky-500/5 py-1.5 rounded">
          Cột đầu là <b>Cần làm</b>, cột cuối là <b>Đã xong</b>. Các cột ở giữa chỉ là tiến độ phụ và vẫn được tính là <b>Đang làm</b>.
        </p>
        {list.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">Chưa có nhóm. Tạo nhóm trước ở nút "Nhóm".</p>
        ) : (
          <>
            {list.length > 1 ? (
              <label className="text-xs block">Áp dụng cho nhóm
                <Select value={cur?.id ?? ""} onChange={(e) => setSelId(e.target.value)} className="mt-1">
                  {list.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </label>
            ) : null}

            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-muted/40 border border-border/50">
                <span className="flex-1 font-medium">Cần làm</span>
                <span className="text-[10px] text-muted-foreground">cố định</span>
              </div>
              {middle.map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={label}
                    onChange={(e) => { const n = [...middle]; n[i] = e.target.value; setMiddle(n); }}
                    className="flex-1"
                  />
                  <DangerBtn type="button" onClick={() => setMiddle(middle.filter((_, j) => j !== i))}>Xoá</DangerBtn>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-muted/40 border border-border/50">
                <span className="flex-1 font-medium">Đã xong</span>
                <span className="text-[10px] text-muted-foreground">cố định</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
                placeholder="Tên cột mới (vd: Đang sơn)"
                className="flex-1"
              />
              <GhostBtn type="button" onClick={handleAdd}>+ Thêm</GhostBtn>
            </div>

            <div className="flex flex-wrap justify-between items-center gap-2 pt-2 border-t border-border/50">
              <GhostBtn type="button" onClick={() => setMiddle([...PROPS_MIDDLE])}>Áp dụng mẫu Làm props</GhostBtn>
              <div className="flex gap-2">
                <GhostBtn type="button" onClick={onClose}>Huỷ</GhostBtn>
                <PrimaryBtn type="button" onClick={handleSave} disabled={!cur || save.isPending}>
                  {save.isPending ? "Đang lưu…" : "Lưu"}
                </PrimaryBtn>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ----- FieldsDialog: tạo/sửa/xoá custom_fields cho project ----- */
function FieldsDialog({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const fields = useCustomFields(projectId);
  const [name, setName] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const [opts, setOpts] = useState("");
  const inv = () => qc.invalidateQueries({ queryKey: ["task_custom_fields", projectId] });
  const create = useMutation({
    mutationFn: () => api.post("/tasks/fields", {
      project_id: projectId, name: name.trim(), type,
      options: type === "select" ? opts.split(",").map((s) => s.trim()).filter(Boolean) : [],
      sort_order: fields.data?.length ?? 0,
    }),
    onSuccess: () => { setName(""); setOpts(""); inv(); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CustomField> }) =>
      api.put(`/tasks/fields/${encodeURIComponent(id)}`, patch),
    onSuccess: () => inv(),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/tasks/fields/${encodeURIComponent(id)}`),
    onSuccess: () => inv(),
  });
  const applyPreset = useMutation({
    mutationFn: async (preset: "order" | "photo" | "rental" | "craft") => {
      const PRESETS: Record<string, { name: string; type: FieldType; key?: string; options?: string[] }[]> = {
        order: [
          { name: "Nguồn order",  type: "select", key: "order_source", options: ["Alibaba", "Taobao", "Xianyu", "Khác"] },
          { name: "Số tiền (CNY)",type: "number", key: "cny_amount" },
          { name: "Số tiền (VND)",type: "number", key: "vnd_amount" },
          { name: "Tracking TQ",  type: "text",   key: "tracking_cn" },
          { name: "Tracking VN",  type: "text",   key: "tracking_vn" },
          { name: "Doanh thu tách",type: "checkbox", key: "isolated_revenue" },
        ],
        rental: [
          { name: "Concept",      type: "text",   key: "concept" },
          { name: "Địa điểm",     type: "text",   key: "location" },
          { name: "Ghi chú khách",type: "text",   key: "customer_note" },
        ],
        craft: [
          { name: "Vật liệu",     type: "text",   key: "materials" },
          { name: "Tiến độ (%)",  type: "number", key: "progress_pct" },
        ],
        photo: [
          { name: "Concept",      type: "text",   key: "concept" },
          { name: "Địa điểm",     type: "text",   key: "location" },
          { name: "Giờ chụp",     type: "text",   key: "shoot_time" },
          { name: "Tiền cọc",     type: "number", key: "deposit" },
          { name: "Còn lại",      type: "number", key: "remaining" },
        ],
      };
      const existingKeys = new Set((fields.data ?? []).map((f) => (f.key ?? f.name).toLowerCase()));
      let base = fields.data?.length ?? 0;
      for (const f of PRESETS[preset]) {
        if (existingKeys.has((f.key ?? f.name).toLowerCase())) continue;
        await api.post("/tasks/fields", {
          project_id: projectId, name: f.name, type: f.type, key: f.key ?? null,
          options: f.options ?? [], sort_order: base++,
        });
      }
    },
    onSuccess: () => inv(),
  });
  return (
    <Modal title="Quản lý Trường nhập (custom fields)" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground border-l-2 border-amber-500/60 pl-2 bg-amber-500/5 py-1.5 rounded">
          Trường nhập là dữ liệu phụ trong form task, không phải cột Board. Để chỉnh cột Board, đóng modal này và bấm nút "Cột Board".
        </p>
        <div className="flex flex-wrap items-center gap-2 rounded border border-border/60 bg-muted/30 p-2 text-xs">
          <span className="text-muted-foreground">Mẫu nhanh:</span>
          <GhostBtn type="button" disabled={applyPreset.isPending} onClick={() => applyPreset.mutate("rental")}>+ Cho thuê</GhostBtn>
          <GhostBtn type="button" disabled={applyPreset.isPending} onClick={() => applyPreset.mutate("craft")}>+ Làm đồ</GhostBtn>
          <GhostBtn type="button" disabled={applyPreset.isPending} onClick={() => applyPreset.mutate("photo")}>+ Chụp ảnh</GhostBtn>
          <GhostBtn type="button" disabled={applyPreset.isPending} onClick={() => applyPreset.mutate("order")}>+ Order (CNY/VND/Tracking)</GhostBtn>
          {applyPreset.isPending ? <span className="text-muted-foreground">Đang thêm…</span> : null}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <label className="text-xs sm:col-span-2">Tên field
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="vd: Concept, Tổng tiền" className="mt-1" />
          </label>
          <label className="text-xs">Loại
            <Select value={type} onChange={(e) => setType(e.target.value as FieldType)} className="mt-1">
              <option value="text">text</option><option value="number">number</option>
              <option value="date">date</option><option value="select">select</option><option value="checkbox">checkbox</option>
            </Select>
          </label>
          <PrimaryBtn type="submit" disabled={create.isPending}>+ Thêm</PrimaryBtn>
          {type === "select" ? (
            <label className="text-xs sm:col-span-4">Tuỳ chọn (phân cách dấu phẩy)
              <Input value={opts} onChange={(e) => setOpts(e.target.value)} placeholder="vd: nhỏ, vừa, lớn" className="mt-1" />
            </label>
          ) : null}
        </form>
        <div className="space-y-1.5 max-h-[50vh] overflow-auto">
          {(fields.data ?? []).map((f) => (
            <div key={f.id} className="flex gap-2 items-center rounded border border-border bg-card p-2 text-xs">
              <span className="font-mono text-[10px] text-muted-foreground w-20 truncate">{f.key ?? f.id.slice(0, 8)}</span>
              <Input defaultValue={f.name} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== f.name) update.mutate({ id: f.id, patch: { name: e.target.value.trim() } }); }} className="flex-1" />
              <span className="px-2 py-0.5 rounded bg-muted text-[10px]">{f.type}</span>
              {f.type === "select" ? (
                <Input defaultValue={(f.options ?? []).join(", ")} placeholder="opt1, opt2"
                  onBlur={(e) => update.mutate({ id: f.id, patch: { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                  className="w-40" />
              ) : null}
              <ConfirmBtn message={`Xoá field "${f.name}"?`} onConfirm={() => del.mutate(f.id)}>Xoá</ConfirmBtn>
            </div>
          ))}
          {(fields.data?.length ?? 0) === 0 && <p className="text-sm italic text-muted-foreground">Chưa có field tuỳ chỉnh.</p>}
        </div>
        <div className="flex justify-end">
          <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
        </div>
      </div>
    </Modal>
  );
}

/* ----- CustomFieldsInput: render dynamic input cho mỗi custom field theo type ----- */
function CustomFieldsInput({ fields, values, onChange }: {
  fields: CustomField[]; values: Record<string, unknown>; onChange: (next: Record<string, unknown>) => void;
}) {
  if (fields.length === 0) return null;
  function set(id: string, v: unknown) { onChange({ ...values, [id]: v }); }
  return (
    <div className="grid gap-3 sm:grid-cols-2 rounded border border-border/60 bg-muted/20 p-3">
      <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trường tuỳ chỉnh</div>
      {fields.map((f) => {
        const v = values[f.id];
        if (f.type === "text") return <label key={f.id} className="text-sm">{f.name}<Input value={String(v ?? "")} onChange={(e) => set(f.id, e.target.value)} className="mt-1" /></label>;
        if (f.type === "number") return <label key={f.id} className="text-sm">{f.name}<Input type="number" value={v === undefined || v === null ? "" : String(v)} onChange={(e) => set(f.id, e.target.value === "" ? null : Number(e.target.value))} className="mt-1" /></label>;
        if (f.type === "date") return <label key={f.id} className="text-sm">{f.name}<DateInputVN value={String(v ?? "")} onChange={(iso) => set(f.id, iso || null)} className="mt-1" /></label>;
        if (f.type === "checkbox") return (
          <label key={f.id} className="text-sm inline-flex items-center gap-2 mt-1">
            <input type="checkbox" checked={!!v} onChange={(e) => set(f.id, e.target.checked)} />
            <span>{f.name}</span>
          </label>
        );
        if (f.type === "select") return (
          <label key={f.id} className="text-sm">{f.name}
            <Select value={String(v ?? "")} onChange={(e) => set(f.id, e.target.value || null)} className="mt-1">
              <option value="">—</option>
              {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
          </label>
        );
        return null;
      })}
    </div>
  );
}



/* ----- CSV import/export tasks (port từ admin.tasks): dùng csvEscape/parseCsv từ shared/csv. ----- */
function exportTasksCsv(
  tasks: Task[],
  sectionMap: Map<string, TaskSection>,
  customerMap: Map<string, string>,
  customerById?: Map<string, Customer>,
) {
  const headers = [
    "title","section","status","priority",
    "customer","customer_phone","customer_address","customer_facebook","customer_instagram",
    "start_date","due_date","rental_date",
    "total_amount","deposit_amount","remaining_amount",
    "tags","description",
    "sample_shot","shared","payment_received","archived_at","completed_at",
  ];
  const num = (v: unknown) => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? String(Math.round(n)) : "";
  };
  const rows = tasks.map((t) => {
    const cust = t.customer_id ? (customerById?.get(t.customer_id) ?? null) : null;
    const cv = (t.custom_values ?? {}) as Record<string, unknown>;
    const tAny = t as unknown as Record<string, unknown>;
    const total   = (tAny.total_amount   as number | undefined) ?? (cv.total_amount as number | undefined) ?? (cv.total as number | undefined) ?? (cv.price as number | undefined);
    const deposit = (tAny.deposit_amount as number | undefined) ?? (cv.deposit as number | undefined);
    const totalN = Number(total) || 0;
    const depN   = Number(deposit) || 0;
    const remN   = totalN > 0 ? Math.max(0, totalN - depN) : 0;
    return [
      t.title,
      t.section_id ? (sectionMap.get(t.section_id)?.name ?? "") : "",
      t.status ?? "", t.priority ?? "",
      cust?.name ?? (t.customer_id ? (customerMap.get(t.customer_id) ?? "") : ""),
      cust?.phone ?? "",
      cust?.address ?? "",
      cust?.facebook ?? "",
      cust?.instagram ?? "",
      fmtDate(t.start_date), fmtDate(t.due_date),
      fmtDate((cv.rental_date as string | undefined) ?? ""),
      num(totalN), num(depN), num(remN),
      (t.tags ?? []).join("|"),
      t.description ?? "",
      (tAny.sample_shot      ? "1" : "0"),
      (tAny.shared           ? "1" : "0"),
      (tAny.payment_received ? "1" : "0"),
      (tAny.archived_at as string | undefined) ?? "",
      (tAny.completed_at as string | undefined) ?? "",
    ];
  });
  const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `tasks-${todayVN()}.csv`;
  a.click(); URL.revokeObjectURL(url);
}
async function importTasksCsv(file: File, projectId: string, sections: TaskSection[]): Promise<number> {
  const text = (await file.text()).replace(/^\ufeff/, "");
  const rows = parseCsv(text);
  if (rows.length < 2) return 0;
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (k: string) => headers.indexOf(k);
  const secByName = new Map(sections.map((s) => [s.name.toLowerCase(), s.id] as const));
  // Tải danh sách khách 1 lần để map theo FB / IG / phone — tạo mới nếu chưa có.
  let allCustomers: Customer[] = [];
  try { allCustomers = await api.get<Customer[]>("/customers"); } catch { /* ignore */ }
  const norm = (s: string) => s.trim().toLowerCase();
  const byFb = new Map<string, Customer>(); const byIg = new Map<string, Customer>();
  const byPhone = new Map<string, Customer>(); const byName = new Map<string, Customer>();
  for (const c of allCustomers) {
    if (c.facebook)  byFb.set(norm(c.facebook), c);
    if (c.instagram) byIg.set(norm(c.instagram), c);
    if (c.phone)     byPhone.set(norm(c.phone), c);
    if (c.name)      byName.set(norm(c.name), c);
  }
  const getCell = (row: string[], k: string) => {
    const i = idx(k);
    return i >= 0 ? (row[i] ?? "").trim() : "";
  };
  const toNum = (s: string): number => {
    if (!s) return 0;
    const n = Number(String(s).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const normalizeCsvDate = (raw: string, rowNum: number, field: string): string => {
    if (!raw.trim()) return "";
    const iso = parseVNDateToISO(raw);
    if (!iso) throw new Error(`Dòng ${rowNum}: ${field} phải là DD/MM/YYYY hoặc YYYY-MM-DD`);
    return iso;
  };
  let n = 0;
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const title = (row[idx("title")] ?? "").trim();
    if (!title) continue;
    const secName = (row[idx("section")] ?? "").trim().toLowerCase();

    // Khách: tìm theo FB/IG/Phone/Name; tạo mới nếu có thông tin liên hệ mà chưa có.
    const cName = getCell(row, "customer");
    const cPhone = getCell(row, "customer_phone");
    const cAddr  = getCell(row, "customer_address");
    const cFb    = getCell(row, "customer_facebook");
    const cIg    = getCell(row, "customer_instagram");
    let customerId: string | null = null;
    const found =
      (cFb && byFb.get(norm(cFb))) ||
      (cIg && byIg.get(norm(cIg))) ||
      (cPhone && byPhone.get(norm(cPhone))) ||
      (cName && byName.get(norm(cName))) || null;
    if (found) customerId = found.id;
    else if (cName || cPhone || cFb || cIg) {
      const displayName = cName || cFb || cIg || cPhone || "Khách";
      try {
        let created: Customer;
        try {
          created = await api.post<Customer>("/customers", {
            name: displayName,
            phone: cPhone || null,
            address: cAddr || null,
            facebook: cFb || null,
            instagram: cIg || null,
          });
        } catch (e) {
          // Xung đột định danh → auto-gộp vào candidate đầu (vì cùng SĐT/FB/IG = cùng người).
          const cands = extractConflictCandidates(e);
          if (cands && cands.length > 0) {
            created = await mergeDraftIntoCustomer(cands[0], {
              phone: cPhone, facebook: cFb, instagram: cIg, address: cAddr,
            });
          } else { throw e; }
        }
        if (created?.id) {
          customerId = created.id;
          allCustomers.push(created);
          if (cFb) byFb.set(norm(cFb), created);
          if (cIg) byIg.set(norm(cIg), created);
          if (cPhone) byPhone.set(norm(cPhone), created);
          if (cName) byName.set(norm(cName), created);
        }
      } catch { /* bỏ qua, vẫn import task không có khách */ }
    }

    // Tiền + ngày thuê
    const startDate = normalizeCsvDate(getCell(row, "start_date"), r + 1, "start_date");
    const dueDate = normalizeCsvDate(getCell(row, "due_date"), r + 1, "due_date");
    const rentalDate = normalizeCsvDate(getCell(row, "rental_date"), r + 1, "rental_date");
    const totalN = toNum(getCell(row, "total_amount"));
    const depN   = toNum(getCell(row, "deposit_amount"));
    const remN   = totalN > 0 ? Math.max(0, totalN - depN) : 0;
    const custom: Record<string, unknown> = {};
    if (rentalDate) custom.rental_date = rentalDate;
    if (totalN > 0) { custom.total_amount = totalN; custom.total = totalN; custom.price = totalN; }
    if (depN > 0) custom.deposit = depN;
    if (totalN > 0) custom.remaining = remN;

    const body: Record<string, unknown> = {
      project_id: projectId,
      title,
      section_id: secName ? (secByName.get(secName) ?? null) : null,
      status: (row[idx("status")] ?? "todo") || "todo",
      priority: (row[idx("priority")] ?? "normal") || "normal",
      start_date: startDate || null,
      due_date: dueDate || null,
      tags: (row[idx("tags")] ?? "").split("|").map((t) => t.trim()).filter(Boolean),
      description: (row[idx("description")] ?? "") || null,
      customer_id: customerId,
      total_amount: totalN,
      deposit_amount: depN,
      remaining_amount: remN,
      custom_values: custom,
    };
    // Cờ trạng thái (đồng bộ với export).
    const truthy = (s: string) => ["1","true","yes","x","y"].includes(s.trim().toLowerCase());
    const sample = truthy(getCell(row, "sample_shot"));
    const shared = truthy(getCell(row, "shared"));
    const paid   = truthy(getCell(row, "payment_received"));
    if (sample) body.sample_shot = true;
    if (shared) body.shared = true;
    if (paid)   body.payment_received = true;
    const archivedAt = getCell(row, "archived_at");
    const completedAt = getCell(row, "completed_at");
    if (archivedAt)  body.archived_at = archivedAt;
    if (completedAt) body.completed_at = completedAt;
    await api.post("/tasks", body); n++;
  }
  return n;
}

export function TasksPage({ onNavigate: _onNav }: { onNavigate: (p: Page) => void }) {
  const qc = useQueryClient();
  const projects = useQuery({ queryKey: ["task_projects"], queryFn: () => api.get<Project[]>("/tasks/projects") });
  const [projectId, setProjectId] = useState<string | null>(null);
  const [stream] = useCurrentStream();
  const visibleProjects = useMemo(
    () => (projects.data ?? []).filter((p) => getProjectStream(p) === stream),
    [projects.data, stream]
  );
  // Ưu tiên project "Làm đồ" / "Làm props" làm mặc định khi mở trang Quản lý task.
  const isMakeProject = (p: Project) => {
    if ((p as any).project_group === "lam-do") return true;
    const n = (p.name || "").toLowerCase();
    return n.includes("làm đồ") || n.includes("lam do") || n.includes("làm props") || n.includes("lam props");
  };
  const pickDefaultTaskProject = (list: Project[]): Project | null => {
    if (!list.length) return null;
    return list.find(isMakeProject) ?? list[0];
  };
  // Khi đổi stream → reset project: ưu tiên "Làm đồ", fallback project đầu tiên thuộc stream.
  useEffect(() => {
    if (!visibleProjects.length) { setProjectId(null); return; }
    if (!projectId || !visibleProjects.find((p) => p.id === projectId)) {
      const pick = pickDefaultTaskProject(visibleProjects);
      if (pick) setProjectId(pick.id);
    }
  }, [visibleProjects, projectId]);
  const [sectionFilter, setSectionFilter] = useState<string | null>(null); // null = tất cả; "__none" = chưa phân nhóm
  const [editing, setEditing] = useState<Task | "new" | null>(null);
  const [viewing, setViewing] = useState<Task | null>(null);
  const [newTaskSection, setNewTaskSection] = useState<string | null | undefined>(undefined);
  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectGroup, setNewProjectGroup] = useState<TaskProjectGroup>("cho-thue");
  const [showSections, setShowSections] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [showBoardCols, setShowBoardCols] = useState(false);
  // View mặc định LUÔN là "Danh sách" khi vào trang, trừ khi URL ép view khác (?view=board|timeline|calendar).
  const [view, setView] = useState<"list" | "board" | "timeline" | "calendar">(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const q = (sp.get("view") || "").toLowerCase();
      if (q === "board" || q === "timeline" || q === "calendar" || q === "list") return q as any;
    } catch {}
    return "list";
  });
  const [showArchived, setShowArchived] = useState<boolean>(() => localStorage.getItem("nana.tasksShowArchived") === "1");
  useEffect(() => { localStorage.setItem("nana.tasksView", view); }, [view]);
  useEffect(() => { localStorage.setItem("nana.tasksShowArchived", showArchived ? "1" : "0"); }, [showArchived]);

  // Backend PHP (db.php) tự seed pinned projects idempotent theo key — frontend không seed.


  useEffect(() => { setSectionFilter(null); }, [projectId]);

  const sections = useSections(projectId);
  const fields = useCustomFields(projectId);
  const tasks = useQuery({
    queryKey: ["tasks", projectId ?? "none"],
    enabled: !!projectId,
    queryFn: () => api.get<Task[]>("/tasks", { project_id: projectId! }),
  });
  const customers = useQuery({ queryKey: ["customers"], queryFn: () => api.get<Customer[]>("/customers") });
  const inventory = useQuery({ queryKey: ["inventory"], queryFn: () => api.get<InventoryItem[]>("/inventory") });

  const customerMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of customers.data ?? []) m.set(c.id, c.name);
    return m;
  }, [customers.data]);
  const customerById = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of customers.data ?? []) m.set(c.id, c);
    return m;
  }, [customers.data]);
  const inventoryById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const i of inventory.data ?? []) m.set(i.id, i);
    return m;
  }, [inventory.data]);
  const sectionMap = useMemo(() => {
    const m = new Map<string, TaskSection>();
    for (const s of sections.data ?? []) m.set(s.id, s);
    return m;
  }, [sections.data]);
  const fieldByKey = useMemo(() => {
    const m = new Map<string, CustomField>();
    for (const f of fields.data ?? []) if (f.key) m.set(f.key, f);
    return m;
  }, [fields.data]);

  const allTasks = tasks.data ?? [];
  const projectsByGroup = useMemo(() => {
    const m = new Map<TaskProjectGroup, Project[]>();
    for (const g of TASK_PROJECT_GROUPS) m.set(g.key, []);
    for (const p of visibleProjects) {
      const key = TASK_PROJECT_GROUPS.some((g) => g.key === p.project_group) ? p.project_group as TaskProjectGroup : "cho-thue";
      m.get(key)!.push(p);
    }
    return m;
  }, [visibleProjects]);
  // Thứ tự nhóm trong sidebar: Làm đồ trước, rồi Cho thuê, Chụp ảnh, Order, Lưu trữ.
  const SIDEBAR_GROUP_ORDER: TaskProjectGroup[] = ["lam-do", "cho-thue", "chup-anh", "order", "luu-tru"];
  const orderedGroups = useMemo(() => {
    const map = new Map(TASK_PROJECT_GROUPS.map((g) => [g.key, g] as const));
    const out: typeof TASK_PROJECT_GROUPS = [];
    for (const k of SIDEBAR_GROUP_ORDER) { const g = map.get(k); if (g) out.push(g); }
    for (const g of TASK_PROJECT_GROUPS) if (!SIDEBAR_GROUP_ORDER.includes(g.key)) out.push(g);
    return out;
  }, []);
  const filteredTasks = useMemo(() => {
    // Lưu trữ: task done & hoàn tất > 7 ngày → ẩn trừ khi bật "Hiện lưu trữ".
    const ARCHIVE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const isArchived = (t: Task) => t.status === "done"
      && !!t.completed_at
      && (now - new Date(t.completed_at).getTime()) > ARCHIVE_AFTER_MS;
    let list = allTasks;
    if (!showArchived) list = list.filter((t) => !isArchived(t));
    if (!sectionFilter) return list;
    if (sectionFilter === "__none") return list.filter((t) => !t.section_id);
    return list.filter((t) => t.section_id === sectionFilter);
  }, [allTasks, sectionFilter, showArchived]);


  const del = useMutation({
    mutationFn: (id: string) => api.del(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks", projectId ?? "none"] }),
  });
  // Map status FE ⇄ section.key (todo/doing/done) — đảm bảo List & Board luôn cùng 1 trạng thái.
  const STATUS_TO_SECTION_KEY: Record<string, string> = { todo: "todo", in_progress: "doing", done: "done" };
  const quickStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => {
      const wantedKey = STATUS_TO_SECTION_KEY[status];
      const target = wantedKey ? (sections.data ?? []).find((s) => s.key === wantedKey) : null;
      const payload: Record<string, unknown> = {
        status,
        completed_at: status === "done" ? new Date().toISOString() : null,
      };
      if (target) payload.section_id = target.id;
      return api.put(`/tasks/${id}`, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks", projectId ?? "none"] }),
  });
  const createProject = useMutation({
    mutationFn: ({ name, group }: { name: string; group: TaskProjectGroup }) => api.post<Project>("/tasks/projects", { name, project_group: group }),
    onSuccess: (p) => {
      setCreatingProject(false); setNewProjectName("");
      qc.invalidateQueries({ queryKey: ["task_projects"] });
      setProjectId(p.id);
    },
  });
  const quickCreate = useMutation({
    mutationFn: ({ title, section_id, status }: { title: string; section_id: string | null; status?: string }) => {
      // Nếu chưa có section_id mà có status → tự suy section khớp status (todo/doing/done) để task không rơi vào "Chưa phân nhóm".
      let finalSection = section_id;
      if (!finalSection && status) {
        const wantedKey = STATUS_TO_SECTION_KEY[status];
        const target = wantedKey ? (sections.data ?? []).find((s) => s.key === wantedKey) : null;
        if (target) finalSection = target.id;
      }
      return api.post("/tasks", {
        project_id: projectId,
        title,
        section_id: finalSection,
        status: status ?? "todo",
        priority: "normal",
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks", projectId ?? "none"] }),
  });

  const currentProject = (projects.data ?? []).find((p) => p.id === projectId);
  const currentGroup = TASK_PROJECT_GROUPS.find((g) => g.key === (currentProject?.project_group ?? "cho-thue")) ?? TASK_PROJECT_GROUPS[0];
  const groupKind = useMemo(() => deriveGroupKind(currentProject ?? null), [currentProject]);

  // Trùng lịch: map taskId → có item nào va chạm với task khác đang active không.
  const conflictByTaskId = useMemo(() => {
    const out = new Map<string, boolean>();
    const overlap = (a1: string | null, a2: string | null, b1: string | null, b2: string | null) => {
      const A1 = a1 || "0000-01-01", A2 = a2 || a1 || "9999-12-31";
      const B1 = b1 || "0000-01-01", B2 = b2 || b1 || "9999-12-31";
      return A1 <= B2 && B1 <= A2;
    };
    const active = allTasks.filter((t) => t.status !== "done" && t.status !== "cancelled");
    for (let i = 0; i < active.length; i++) {
      const a = active[i];
      const aIds = (a.inventory_item_ids ?? []) as string[];
      if (aIds.length === 0) continue;
      for (let j = 0; j < active.length; j++) {
        if (i === j) continue;
        const b = active[j];
        const bIds = (b.inventory_item_ids ?? []) as string[];
        if (bIds.length === 0) continue;
        if (!aIds.some((id) => bIds.includes(id))) continue;
        if (!overlap(a.start_date ?? null, a.due_date ?? null, b.start_date ?? null, b.due_date ?? null)) continue;
        out.set(a.id, true);
        break;
      }
    }
    return out;
  }, [allTasks]);

  const VIEW_TABS: { v: typeof view; label: string; icon: typeof List }[] = [
    { v: "list", label: "Danh sách", icon: List },
    { v: "board", label: "Board", icon: FolderKanban },
    { v: "timeline", label: "Timeline", icon: GanttChartSquare },
    { v: "calendar", label: "Lịch", icon: CalendarDays },
  ];

  return (
    <section className="grid gap-4 lg:grid-cols-[224px_1fr] min-h-[70vh]">
      <aside className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-3 py-3 flex items-center justify-between border-b border-border/60">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Project</div>
          <button onClick={() => setCreatingProject(true)} className="p-1 rounded hover:bg-accent" title="Project mới"><FolderPlus className="h-4 w-4" /></button>
        </div>
        <div className="p-2 space-y-1">
          {orderedGroups.map((g) => {
            const list = projectsByGroup.get(g.key) ?? [];
            const activeGroup = currentGroup.key === g.key;
            return (
              <div key={g.key}>
                <div className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider ${activeGroup ? "text-foreground" : "text-muted-foreground"}`}>
                  <span className={`h-2 w-2 rounded-full ${g.dot}`} />
                  <span className="flex-1 truncate">{g.label}</span>
                  <button
                    onClick={() => { setNewProjectGroup(g.key); setCreatingProject(true); }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground"
                    title={`Thêm project vào ${g.label}`}
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                  </button>
                </div>
                {list.length > 0 ? (
                  <div className="ml-3 border-l border-border/60 py-0.5 mb-1">
                    {list.map((p) => (
                      <button key={p.id} onClick={() => setProjectId(p.id)}
                        className={`block w-full text-left truncate px-3 py-1.5 text-xs rounded-r-md ${p.id === projectId ? "bg-primary/15 text-foreground font-medium" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="ml-3 px-3 py-1 text-[11px] italic text-muted-foreground/60">Chưa có project</div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      <div className="space-y-3 min-w-0">
      <div className="rounded-lg border border-border bg-card">
        <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-3 border-b border-border/60">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`h-3 w-3 rounded-full ${currentGroup.dot}`} />
            <h1 className="text-base font-semibold truncate">
              {currentGroup.label} <span className="text-muted-foreground">/</span> {currentProject?.name ?? "Chọn project"}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <PrimaryBtn onClick={() => { setNewTaskSection(sectionFilter && sectionFilter !== "__none" ? sectionFilter : null); setEditing("new"); }} disabled={!projectId} className="!py-1.5">
              <Plus className="h-3.5 w-3.5 mr-1 inline" />Thêm task
            </PrimaryBtn>
            <GhostBtn onClick={() => setShowFields(true)} disabled={!projectId} className="!py-1.5" title="Field nhập phụ trong form task, không phải cột Board">Trường nhập</GhostBtn>
            <GhostBtn onClick={() => setShowBoardCols(true)} disabled={!projectId} className="!py-1.5" title="Cấu hình cột hiển thị trên Board view">Cột Board</GhostBtn>
            <GhostBtn onClick={() => setShowSections(true)} disabled={!projectId} className="!py-1.5">Nhóm</GhostBtn>
            <GhostBtn onClick={() => exportTasksCsv(filteredTasks, sectionMap, customerMap, customerById)} disabled={!projectId || filteredTasks.length === 0} className="!py-1.5">↓ CSV</GhostBtn>
          </div>
        </div>
        <div className="px-3 py-2 flex items-center gap-1 overflow-x-auto">
          {VIEW_TABS.map(({ v, label, icon: Icon }) => {
            const active = view === v;
            return (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 transition ${active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-1.5 py-1.5 pr-2">
            <GhostBtn onClick={() => setShowArchived((v) => !v)} title="Bật/tắt hiển thị task đã lưu trữ (>7 ngày sau khi Đã xong)">
              {showArchived ? "📦 Đang hiện lưu trữ" : "📦 Ẩn lưu trữ"}
            </GhostBtn>
            <label className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border cursor-pointer hover:bg-accent ${!projectId ? "opacity-50 pointer-events-none" : ""}`}>
              ↑ CSV
              <input type="file" accept=".csv,text/csv" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; e.currentTarget.value = "";
                  if (!f || !projectId) return;
                  try {
                    const n = await importTasksCsv(f, projectId, sections.data ?? []);
                    alert(`Đã import ${n} task.`);
                    qc.invalidateQueries({ queryKey: ["tasks", projectId] });
                  } catch (err) { alert(getErrorMessage(err, "Import thất bại")); }
                }} />
            </label>
          </div>
        </div>
      </div>

      {tasks.error ? <ErrorBox message={getErrorMessage(tasks.error, "Không tải được tasks")} /> : null}
      {projects.error ? <ErrorBox message={getErrorMessage(projects.error, "Không tải được projects")} /> : null}

      {/* Section filter chips đã bỏ — danh sách hiển thị tất cả task, đổi trạng thái bằng nút trên mỗi task. */}

      <div className="rounded-lg border border-border bg-card min-w-0">
        {tasks.isFetching && filteredTasks.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground inline-flex items-center gap-2 justify-center w-full"><Loader2 className="h-3.5 w-3.5 animate-spin" />Đang tải…</div>
        ) : !projectId ? (
          <p className="p-6 text-sm text-muted-foreground text-center">Hãy chọn hoặc tạo một project.</p>
        ) : view === "timeline" ? (
          <TaskTimelineView tasks={filteredTasks} onEdit={(t) => setEditing(t)} />
        ) : view === "calendar" ? (
          <TaskCalendarView tasks={filteredTasks} onEdit={(t) => setEditing(t)} onNew={(d) => setEditing({ due_date: d } as any)} />
        ) : view === "board" ? (
          <TaskBoardView
            tasks={filteredTasks}
            section={sectionFilter && sectionFilter !== "__none" ? sectionMap.get(sectionFilter) ?? null : null}
            allSections={sections.data ?? []}
            customFields={fields.data ?? []}
            onEdit={(t) => setEditing(t)}
            onStatusChange={(id, status) => quickStatus.mutate({ id, status })}
            onOpenFields={() => setShowFields(true)}
            onQuickCreate={(status) => quickCreate.mutate({ title: "Việc mới", section_id: sectionFilter && sectionFilter !== "__none" ? sectionFilter : null, status })}
            quickCreateInline={(title, status) => quickCreate.mutateAsync({ title, section_id: sectionFilter && sectionFilter !== "__none" ? sectionFilter : null, status })}
          />
        ) : (
          <TaskListGrouped
            tasks={filteredTasks}
            sections={sections.data ?? []}
            groupKind={groupKind}
            fieldByKey={fieldByKey}
            customerMap={customerMap}
            customerById={customerById}
            inventoryById={inventoryById}
            conflictByTaskId={conflictByTaskId}
            onEdit={(t) => setViewing(t)}
            onDelete={async (t) => { if (await confirmAsync(`Xóa "${t.title}"?`)) del.mutate(t.id); }}
            onToggleDone={(t) => quickStatus.mutate({ id: t.id, status: t.status === "done" ? "todo" : "done" })}
            onChangeStatus={(t, status) => quickStatus.mutate({ id: t.id, status })}
            onOpenChecklist={(t) => setEditing(t)}
            onNewIn={(sid) => { setNewTaskSection(sid); setEditing("new"); }}
            onQuickCreate={(sectionId, title) => quickCreate.mutateAsync({ title, section_id: sectionId })}
          />
        )}
      </div>

      {viewing ? (
        <TaskDetail
          task={viewing}
          customerMap={customerMap}
          sections={sections.data ?? []}
          fieldByKey={fieldByKey}
          projects={projects.data ?? []}
          currentProject={currentProject ?? null}
          groupKind={groupKind}
          projectId={projectId ?? ""}
          onClose={() => setViewing(null)}
          onEdit={() => { const t = viewing; setViewing(null); setEditing(t); }}
          onDelete={async () => { if (await confirmAsync(`Xóa "${viewing.title}"?`)) { del.mutate(viewing.id); setViewing(null); } }}
        />
      ) : null}

      {editing && projectId ? (
        <TaskForm
          projectId={projectId}
          groupKind={groupKind}
          initial={editing === "new" ? ({ section_id: newTaskSection ?? null } as any) : editing}
          customers={customers.data ?? []}
          sections={sections.data ?? []}
          fields={fields.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["tasks", projectId] }); }}
        />
      ) : null}

      {creatingProject ? (
        <Modal title="Project mới" onClose={() => setCreatingProject(false)}>
          <form onSubmit={(e) => { e.preventDefault(); if (newProjectName.trim()) createProject.mutate({ name: newProjectName.trim(), group: newProjectGroup }); }} className="space-y-3">
            <label className="block text-sm">Tên project<Input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} autoFocus className="mt-1" /></label>
            <label className="block text-sm">Nhóm
              <Select value={newProjectGroup} onChange={(e) => setNewProjectGroup(e.target.value as TaskProjectGroup)} className="mt-1">
                {TASK_PROJECT_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
              </Select>
            </label>
            {createProject.error ? <ErrorBox message={getErrorMessage(createProject.error, "Lưu thất bại")} /> : null}
            <div className="flex justify-end gap-2">
              <GhostBtn type="button" onClick={() => setCreatingProject(false)}>Hủy</GhostBtn>
              <PrimaryBtn type="submit" disabled={createProject.isPending}>{createProject.isPending ? "…" : "Tạo"}</PrimaryBtn>
            </div>
          </form>
        </Modal>
      ) : null}
      {showSections && projectId ? <SectionsDialog projectId={projectId} onClose={() => setShowSections(false)} /> : null}
      {showFields && projectId ? <FieldsDialog projectId={projectId} onClose={() => setShowFields(false)} /> : null}
      {showBoardCols && projectId ? (
        <BoardColumnsDialog
          projectId={projectId}
          currentProjectName={currentProject?.name ?? ""}
          preferSectionId={sectionFilter}
          onClose={() => setShowBoardCols(false)}
        />
      ) : null}
      </div>
    </section>
  );
}

/* Countdown from due_date — UI only, no business logic change.
   Tone bậc thang theo số ngày còn lại để dễ ưu tiên đơn gấp. */
type CountdownTone = "overdue"|"today"|"critical"|"warning"|"soon"|"normal"|"safe"|"none";
function countdownFromDue(due?: string | null): { label: string; tone: CountdownTone } {
  if (!due) return { label: "Chưa có hạn", tone: "none" };
  const d = new Date(due + "T00:00:00");
  if (isNaN(d.getTime())) return { label: "Chưa có hạn", tone: "none" };
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0)   return { label: `Trễ ${-diff} ngày`, tone: "overdue" };
  if (diff === 0) return { label: "Hôm nay", tone: "today" };
  if (diff <= 3)  return { label: `Còn ${diff} ngày`, tone: "critical" };
  if (diff <= 7)  return { label: `Còn ${diff} ngày`, tone: "warning" };
  if (diff <= 14) return { label: `Còn ${diff} ngày`, tone: "soon" };
  if (diff <= 30) return { label: `Còn ${diff} ngày`, tone: "normal" };
  return { label: `Còn ${diff} ngày`, tone: "safe" };
}
const COUNTDOWN_CLS: Record<CountdownTone, string> = {
  overdue:  "bg-red-500/25 text-red-200 border-red-500/60 font-bold",
  today:    "bg-red-500/20 text-red-300 border-red-500/60 font-bold",
  critical: "bg-orange-500/20 text-orange-300 border-orange-500/55 font-semibold",
  warning:  "bg-amber-500/20 text-amber-300 border-amber-500/55 font-semibold",
  soon:     "bg-yellow-500/15 text-yellow-300 border-yellow-500/45 font-medium",
  normal:   "bg-sky-500/15 text-sky-300 border-sky-500/40 font-medium",
  safe:     "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 font-medium",
  none:     "text-muted-foreground/60 border-dashed border-border bg-transparent",
};

/* ----- TaskListGrouped: flat list + inline status pill + count bar (Linear/Asana-style) ----- */
function TaskListGrouped({ tasks, groupKind, fieldByKey, customerMap, customerById, inventoryById, conflictByTaskId, onEdit, onDelete, onChangeStatus, onOpenChecklist, onNewIn, onQuickCreate }: {
  tasks: Task[]; sections: TaskSection[];
  groupKind?: GroupKind;
  fieldByKey: Map<string, CustomField>;
  customerMap: Map<string, string>;
  customerById?: Map<string, Customer>;
  inventoryById?: Map<string, InventoryItem>;
  conflictByTaskId?: Map<string, boolean>;
  onEdit: (t: Task) => void; onDelete: (t: Task) => void;
  onToggleDone: (t: Task) => void;
  onChangeStatus?: (t: Task, status: string) => void;
  onOpenChecklist?: (t: Task) => void;
  onNewIn: (sectionId: string | null) => void;
  onQuickCreate: (sectionId: string | null, title: string) => Promise<unknown>;
}) {
  type Norm = "todo" | "doing" | "done" | "other";
  const normStatus = (s: string | null | undefined): Norm => {
    const v = (s || "todo").toLowerCase();
    if (v === "done" || v === "cancelled") return "done";
    if (v === "in_progress" || v === "doing") return "doing";
    if (v === "todo") return "todo";
    return "other";
  };
  const STATUS_CYCLE: Record<Norm, string> = { todo: "in_progress", doing: "done", done: "todo", other: "todo" };

  const counts = useMemo(() => {
    let todo = 0, doing = 0, done = 0;
    for (const t of tasks) {
      const n = normStatus(t.status);
      if (n === "done") done++; else if (n === "doing") doing++; else todo++;
    }
    return { todo, doing, done, total: tasks.length };
  }, [tasks]);

  const [filter, setFilter] = useState<"all" | Norm>("all");
  const [showDone, setShowDone] = useState(true); // Luôn hiện task đã xong (đã bỏ filter bar)

  const visibleTasks = useMemo(() => {
    const order: Record<Norm, number> = { todo: 0, doing: 1, other: 2, done: 3 };
    const list = tasks.filter((t) => {
      const n = normStatus(t.status);
      if (filter === "all") {
        if (n === "done" && !showDone) return false;
        return true;
      }
      return n === filter;
    });
    return [...list].sort((a, b) => {
      const da = order[normStatus(a.status)]; const db = order[normStatus(b.status)];
      if (da !== db) return da - db;
      const ad = a.due_date || "9999-99-99"; const bd = b.due_date || "9999-99-99";
      return ad.localeCompare(bd);
    });
  }, [tasks, filter, showDone]);

  function val(t: Task, key: string): unknown {
    const f = fieldByKey.get(key);
    return t.custom_values?.[key] ?? (f ? t.custom_values?.[f.id] : undefined);
  }
  function fmtMoney(n: unknown): string | null {
    const x = Number(n);
    if (!Number.isFinite(x) || x === 0) return null;
    return x.toLocaleString("vi-VN") + "₫";
  }
  const PRIO_BADGE: Record<string, { label: string; cls: string }> = {
    low:    { label: "Thấp",  cls: "bg-slate-500/15 text-slate-400 border border-slate-500/30" },
    normal: { label: "Vừa",   cls: "bg-blue-500/15 text-blue-400 border border-blue-500/30" },
    med:    { label: "Vừa",   cls: "bg-blue-500/15 text-blue-400 border border-blue-500/30" },
    high:   { label: "Cao",   cls: "bg-orange-500/20 text-orange-400 border border-orange-500/30" },
    urgent: { label: "Khẩn",  cls: "bg-red-500/20 text-red-400 border border-red-500/40" },
  };
  const STATUS_PILL: Record<Norm, { label: string; cls: string; icon: ReactNode }> = {
    todo:  { label: "Cần làm",  cls: "bg-slate-500/15 text-slate-300 border-slate-500/40",   icon: <Circle className="h-3 w-3" /> },
    doing: { label: "Đang làm", cls: "bg-blue-500/15 text-blue-400 border-blue-500/40",       icon: <Clock className="h-3 w-3" /> },
    done:  { label: "Đã xong",  cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40", icon: <CheckCircle2 className="h-3 w-3" /> },
    other: { label: "Khác",     cls: "bg-slate-500/15 text-slate-400 border-slate-500/40",   icon: <Circle className="h-3 w-3" /> },
  };

  function FilterPill({ k, label, count, color }: { k: "all" | Norm; label: string; count: number; color: string }) {
    const active = filter === k;
    return (
      <button onClick={() => setFilter(k)}
        className={`px-2.5 py-1 rounded-full text-xs border transition inline-flex items-center gap-1 ${active ? `${color} font-semibold` : "bg-card border-border hover:bg-accent text-muted-foreground"}`}>
        {label} · {count}
      </button>
    );
  }

  return (
    <div>
      {/* Bỏ thanh filter trạng thái — đổi trạng thái bằng nút pill ngay trên mỗi task. */}


      {/* Danh sách phẳng */}
      <div className="space-y-1.5 p-3">
        {visibleTasks.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">Không có công việc.</div>
        ) : visibleTasks.map((t) => {
          const n = normStatus(t.status);
          const pill = STATUS_PILL[n];
          const done = n === "done";
          const cust = t.customer_id ? customerById?.get(t.customer_id) : null;
          const custName = cust?.name ?? (t.customer_id ? customerMap.get(t.customer_id) : null);
          const phone = cust?.phone;
          const address = cust?.address;
          const items = (t.inventory_item_ids ?? [])
            .map((id) => inventoryById?.get(id))
            .filter(Boolean) as InventoryItem[];
          const total = fmtMoney(val(t, "total_amount") ?? (t.custom_values as any)?.price);
          const deposit = fmtMoney(val(t, "deposit"));
          const remaining = fmtMoney(val(t, "remaining"));
          const note = val(t, "customer_note") ?? t.description;
          const character = val(t, "character");
          const location = val(t, "location");
          const orderLink = val(t, "order_link");
          const orderDesc = val(t, "order_description");
          const conflict = conflictByTaskId?.get(t.id);
          const prio = PRIO_BADGE[t.priority ?? "normal"];
          // Date display: ưu tiên start/due, fallback rental_date / return_date / shoot_time
          // để task vẫn hiện ngày khi user lưu vào field khác (custom_values.rental_date,
          // hoặc cột return_date / shoot_time trên task). Không bao giờ "ẩn ngày đã lưu".
          const tCv = (t.custom_values ?? {}) as Record<string, unknown>;
          const tAnyRow = t as any;
          const rentalDateVal = typeof tCv.rental_date === "string" ? tCv.rental_date : "";
          const returnDateVal = typeof tAnyRow.return_date === "string" ? tAnyRow.return_date : "";
          const shootTimeVal  = typeof tAnyRow.shoot_time  === "string" ? tAnyRow.shoot_time  : "";
          let dateRange = "";
          if (t.start_date && t.due_date) {
            dateRange = t.start_date === t.due_date
              ? fmtDate(t.start_date)
              : `${fmtDate(t.start_date)} → ${fmtDate(t.due_date)}`;
          } else if (t.start_date || t.due_date) {
            dateRange = fmtDate(t.start_date || t.due_date) || "";
          } else if (rentalDateVal) {
            dateRange = `Ngày thuê: ${fmtDate(rentalDateVal)}`;
          }
          const dateExtra: string[] = [];
          if (returnDateVal) dateExtra.push(`Trả: ${fmtDate(returnDateVal)}`);
          if (shootTimeVal)  dateExtra.push(`Giờ chụp: ${shootTimeVal}`);
          // Checklist progress (chỉ chụp ảnh)
          const checklist = Array.isArray((t.custom_values as any)?.[CHECKLIST_KEY])
            ? ((t.custom_values as any)[CHECKLIST_KEY] as Array<{ checked?: boolean }>) : [];
          const checklistTotal = checklist.length;
          const checklistDone = checklist.filter((x) => x?.checked).length;

          // Tiêu đề ưu tiên: khách hàng > tiêu đề tự sinh
          const primary = custName || t.title || "(Việc mới)";
          const showTitleAsSub = custName && t.title && !t.title.toLowerCase().startsWith((custName || "").toLowerCase());

          // Sub-line content theo loại project
          let subContent: ReactNode = null;
          if (groupKind === "chup-anh") {
            const bits = [character && `🎭 ${character}`, location && `📍 ${location}`].filter(Boolean);
            if (bits.length) subContent = bits.join("  ·  ");
          } else if (groupKind === "lam-do") {
            if (note) subContent = String(note);
          } else if (groupKind === "order") {
            const bits = [orderDesc && String(orderDesc), orderLink && "🔗 link"].filter(Boolean);
            if (bits.length) subContent = bits.join("  ·  ");
          } else {
            if (items.length > 0) subContent = `📦 ${items.slice(0, 3).map((i) => i.name).join(", ")}${items.length > 3 ? ` +${items.length - 3}` : ""}`;
          }

          // Main content: ưu tiên yêu cầu khách (title). Nếu trống → fallback subContent → "(Việc mới)"
          const mainText = (t.title && t.title.trim()) || (typeof subContent === "string" ? subContent : "") || "(Việc mới)";
          const showSubLine = !!subContent && subContent !== mainText;
          const cd = countdownFromDue(t.due_date);
          return (
            <div key={t.id}
              onClick={() => onEdit(t)}
              className={`group rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                conflict ? "border-red-500/40 bg-red-500/10 hover:bg-red-500/15"
                  : done   ? "border-border/40 bg-card/40 hover:bg-accent/30 opacity-75"
                           : "border-border/60 bg-card hover:bg-accent/30"
              }`}
            >
              <div className="flex flex-col gap-2 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_160px_180px_220px_auto] lg:items-center lg:gap-4">
                {/* Vùng Status — pill click để cycle todo → doing → done */}
                <button
                  onClick={(e) => { e.stopPropagation(); onChangeStatus?.(t, STATUS_CYCLE[n] ?? "todo"); }}
                  className={`self-start lg:self-center shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${pill.cls} hover:brightness-125 w-fit`}
                  title="Click để đổi trạng thái"
                >
                  {pill.icon}{pill.label}
                </button>

                {/* Vùng A — Yêu cầu của khách (nổi bật nhất) */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-base font-bold leading-snug ${done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                      {mainText}
                    </span>
                    {prio ? <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold ${prio.cls}`}>{prio.label}</span> : null}
                    {conflict ? <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-red-500/20 text-red-400 border border-red-500/40 font-semibold">⚠ Trùng lịch</span> : null}
                    {groupKind === "chup-anh" && checklistTotal > 0 ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenChecklist?.(t); }}
                        className={`text-[10px] px-2 py-0.5 rounded-md border inline-flex items-center gap-1 ${
                          checklistDone === checklistTotal
                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                            : "bg-purple-500/15 text-purple-400 border-purple-500/40"
                        }`}
                        title="Mở checklist phụ kiện"
                      >
                        ✓ {checklistDone}/{checklistTotal}
                      </button>
                    ) : null}
                    {(t.tags ?? []).slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">{tag}</span>
                    ))}
                  </div>
                  {showSubLine ? (
                    <div className="mt-0.5 text-xs text-muted-foreground line-clamp-1">{subContent}</div>
                  ) : null}
                  {note && groupKind !== "lam-do" && groupKind !== "order" ? (
                    <div className="mt-0.5 text-[11px] italic text-muted-foreground/80 line-clamp-1">📝 {String(note)}</div>
                  ) : null}
                </div>

                {/* Vùng B — Deadline countdown */}
                <div className="min-w-0 flex flex-col items-start lg:items-center gap-0.5">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-xs whitespace-nowrap ${COUNTDOWN_CLS[cd.tone]}`}>
                    <Clock className="h-3 w-3" />{cd.label}
                  </span>
                  {dateRange ? (
                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />{dateRange}
                    </span>
                  ) : null}
                  {dateExtra.length > 0 ? (
                    <span className="text-[10px] text-muted-foreground/80">
                      {dateExtra.join("  ·  ")}
                    </span>
                  ) : null}
                </div>

                {/* Vùng C — Tiền còn lại (ưu tiên doanh thu tương lai) */}
                <div className="min-w-0 flex flex-col items-start lg:items-end gap-0.5">
                  {remaining ? (
                    <span className="text-sm font-bold text-emerald-400 whitespace-nowrap">Còn {remaining}</span>
                  ) : (total || deposit) ? (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">— đã đủ —</span>
                  ) : (
                    <span className="text-xs text-muted-foreground/50">—</span>
                  )}
                  {(total || deposit) ? (
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {[total && `Tổng ${total}`, deposit && `Cọc ${deposit}`].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </div>

                {/* Vùng D — Liên hệ khách (phụ nhưng đọc/bấm dễ) */}
                <div className="min-w-0 flex flex-col gap-1">
                  {custName ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/90 truncate">
                      <User className="h-3.5 w-3.5 opacity-70 shrink-0" />
                      <span className="truncate">{custName}</span>
                      {(() => {
                        const v = Number((cust as any)?.vip_score ?? 0);
                        if (v <= 0) return null;
                        const cls = v >= 80 ? "bg-amber-500/15 text-amber-600" : v >= 50 ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground";
                        return <span className={`shrink-0 text-[10px] px-1.5 py-0 rounded-full font-medium ${cls}`} title={`VIP ${v}`}>VIP {v}</span>;
                      })()}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground/50">— không có khách —</span>
                  )}
                  {cust ? (() => {
                    const cAny = cust as any;
                    const ctags: string[] = Array.isArray(cAny?.tags) ? cAny.tags.filter(Boolean) : [];
                    const realName: string | null = typeof cAny?.real_name === "string" && cAny.real_name.trim() ? cAny.real_name.trim() : null;
                    const bankNames: string[] = Array.isArray(cAny?.bank_transfer_names)
                      ? cAny.bank_transfer_names.map((s: unknown) => (typeof s === "string" ? s.trim() : "")).filter(Boolean)
                      : [];
                    return (
                      <>
                        {ctags.length > 0 ? (
                          <span className="inline-flex flex-wrap items-center gap-1">
                            {ctags.slice(0, 2).map((t) => (
                              <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">{t}</span>
                            ))}
                            {ctags.length > 2 ? <span className="text-[10px] text-muted-foreground/70">+{ctags.length - 2}</span> : null}
                          </span>
                        ) : null}
                        {realName ? (
                          <span className="text-[11px] italic text-muted-foreground/80 truncate" title={`Tên thật: ${realName}`}>Tên thật: {realName}</span>
                        ) : null}
                        {bankNames.length > 0 ? (
                          <span className="inline-flex flex-wrap items-center gap-1">
                            {bankNames.slice(0, 2).map((t) => (
                              <span key={t} className="text-[10px] px-1.5 py-0 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20" title={`Tên CK: ${t}`}>₫ {t}</span>
                            ))}
                            {bankNames.length > 2 ? <span className="text-[10px] text-muted-foreground/70">+{bankNames.length - 2}</span> : null}
                          </span>
                        ) : null}
                      </>
                    );
                  })() : null}
                  <div className="flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
                    <CustomerContactIcons cust={cust} />
                    {phone && !cust?.phone ? <span className="text-xs text-muted-foreground select-all px-1">{phone}</span> : null}
                  </div>
                  {address ? (
                    <span className="text-[11px] text-muted-foreground/70 truncate" title={address}>📍 {address}</span>
                  ) : null}
                </div>

                {/* Vùng E — Actions */}
                <div className="flex items-center gap-1 lg:opacity-0 group-hover:opacity-100 transition-opacity shrink-0 self-start lg:self-center">
                  <AlbumCell url={t.piwigo_album_url} status={t.album_status} />
                  <button onClick={(e) => { e.stopPropagation(); onEdit(t); }} title="Sửa" className="p-1 rounded hover:bg-accent"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); onDelete(t); }} title="Xoá" className="p-1 rounded hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          );
        })}
        <InlineAddRow onSubmit={(title) => onQuickCreate(null, title)} placeholder="+ Thêm việc… (Enter để lưu, Shift+Enter để mở chi tiết)" onOpenDetail={() => onNewIn(null)} />
      </div>
    </div>
  );
}


/* Inline "+ Add task" row, Asana-style: bấm để hiện ô input, Enter để lưu, Esc để huỷ. */
function InlineAddRow({ onSubmit, placeholder, onOpenDetail }: {
  onSubmit: (title: string) => Promise<unknown> | void;
  placeholder: string;
  onOpenDetail?: () => void;
}) {
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (active) inputRef.current?.focus(); }, [active]);
  async function commit() {
    const t = title.trim();
    if (!t) { setActive(false); return; }
    setBusy(true);
    try { await onSubmit(t); setTitle(""); inputRef.current?.focus(); }
    finally { setBusy(false); }
  }
  if (!active) {
    return (
      <button onClick={() => setActive(true)}
        className="w-full text-left px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/30 inline-flex items-center gap-2 border-t border-border/40">
        <Plus className="h-3.5 w-3.5" /> {placeholder}
      </button>
    );
  }
  return (
    <div className="px-4 py-1.5 border-t border-border/40 bg-accent/10 flex items-center gap-2">
      <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
      <input ref={inputRef} value={title} onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); }
          else if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); onOpenDetail?.(); setActive(false); }
          else if (e.key === "Escape") { setActive(false); setTitle(""); }
        }}
        onBlur={() => { if (!title.trim()) setActive(false); }}
        disabled={busy}
        placeholder="Tên việc — Enter để lưu, Esc để huỷ"
        className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground/60" />
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
    </div>
  );
}

/* ----- TaskBoardView: 3 cột mặc định Cần làm / Đang làm / Đã xong; kéo thả đồng bộ status + section_id ----- */
function TaskBoardView({ tasks, section, allSections, customFields, onEdit, onStatusChange, onOpenFields, quickCreateInline }: {
  tasks: Task[]; section: TaskSection | null;
  allSections?: TaskSection[];
  customFields?: CustomField[];
  onEdit: (t: Task) => void; onStatusChange: (id: string, status: string) => void;
  onOpenFields?: () => void;
  onQuickCreate?: (status: string) => void;
  quickCreateInline: (title: string, status: string) => Promise<unknown>;
}) {
  // Ưu tiên board_columns:
  // A. section đang xem có board_columns → dùng.
  // B. nếu không, tìm section trong project có board_columns không rỗng
  //    (ưu tiên tên chứa "đang làm", rồi tới section có nhiều cột nhất).
  // C. fallback 3 cột chuẩn.
  const pickSectionCols = (): BoardColumn[] | null => {
    if (section?.board_columns?.length) return section.board_columns;
    const list = (allSections ?? []).filter((s) => (s.board_columns?.length ?? 0) > 0);
    if (list.length === 0) return null;
    const doingFirst = list.find((s) => /đang\s*làm/i.test(s.name ?? "") || s.key === "doing");
    if (doingFirst?.board_columns?.length) return doingFirst.board_columns;
    const best = [...list].sort((a, b) => (b.board_columns?.length ?? 0) - (a.board_columns?.length ?? 0))[0];
    return best?.board_columns ?? null;
  };
  const cols: BoardColumn[] = pickSectionCols() ?? [
    { key: "todo", label: "Cần làm", group: "todo" },
    { key: "in_progress", label: "Đang làm", group: "doing" },
    { key: "done", label: "Đã xong", group: "done" },
  ];
  // Khi không chọn section cụ thể → map task qua section.key sang cột status để hiển thị.
  const sectionKeyToStatus: Record<string, string> = { todo: "todo", doing: "in_progress", done: "done" };
  const sectionIdToStatus = new Map<string, string>();
  for (const s of (allSections ?? [])) if (s.key && sectionKeyToStatus[s.key]) sectionIdToStatus.set(s.id, sectionKeyToStatus[s.key]);
  // Nếu status hiện tại không có cột tương ứng (vd cũ "in_progress" sau khi đổi preset),
  // ưu tiên map vào cột group="doing" đầu tiên, không nhồi hết vào "Cần làm".
  const firstDoing = cols.find((c) => c.group === "doing")?.key;
  const fallback = firstDoing ?? cols[0]?.key ?? "todo";
  const groups = new Map<string, Task[]>();
  for (const c of cols) groups.set(c.key, []);
  for (const t of tasks) {
    // Ưu tiên status; nếu trống thì suy từ section_id (cho project pinned có 3 section chuẩn).
    let k = t.status ?? "";
    if (!groups.has(k) && t.section_id && sectionIdToStatus.has(t.section_id)) k = sectionIdToStatus.get(t.section_id)!;
    if (!groups.has(k)) k = fallback;
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(t);
  }
  const groupAccent = (g?: string) =>
    g === "done" ? "bg-emerald-500" : g === "doing" ? "bg-blue-500" : g === "notes" ? "bg-amber-500" : "bg-muted-foreground/50";
  const priColor = (p?: string | null) =>
    p === "urgent" ? "border-l-red-500" : p === "high" ? "border-l-orange-500" : p === "low" ? "border-l-muted-foreground/30" : "border-l-blue-500";

  // Chip nhỏ render giá trị custom field trên card
  const fieldsForCard = (customFields ?? []).slice(0, 4);

  return (
    <div className="flex flex-col">
      {/* Header board: nút thêm trường thủ công */}
      <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between bg-card/40">
        <div className="text-[11px] text-muted-foreground">
          {fieldsForCard.length > 0
            ? <>Trường thủ công: {fieldsForCard.map((f) => f.name).join(" · ")}</>
            : <>Chưa có trường thủ công.</>}
        </div>
        {onOpenFields ? (
          <button onClick={onOpenFields}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border bg-card hover:bg-accent">
            <Plus className="h-3 w-3" /> Trường
          </button>
        ) : null}
      </div>

      <div className="grid gap-3 p-3 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(260px, 1fr))` }}>
        {cols.map((c) => {
          const list = groups.get(c.key) ?? [];
          return (
            <div key={c.key} className="rounded-lg bg-muted/40 p-2 flex flex-col min-h-[200px]"
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("text/task-id");
                if (id) onStatusChange(id, c.key);
              }}
            >
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className={`h-2 w-2 rounded-full ${groupAccent(c.group)}`} />
                <div className="text-xs font-semibold text-foreground">{c.label}</div>
                <span className="text-[10px] text-muted-foreground bg-background/80 rounded-full px-1.5 py-0.5">{list.length}</span>
              </div>
              <div className="space-y-2 flex-1">
                {list.map((t) => {
                  // Custom field chips
                  const chips = fieldsForCard
                    .map((f) => {
                      const v = (t.custom_values as any)?.[f.key ?? f.id] ?? (t.custom_values as any)?.[f.id];
                      if (v === undefined || v === null || v === "" || v === false) return null;
                      const display = f.type === "checkbox" ? (v ? "✓" : "") : String(v);
                      return { name: f.name, value: display };
                    })
                    .filter(Boolean) as Array<{ name: string; value: string }>;
                  return (
                    <div key={t.id} draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/task-id", t.id); e.dataTransfer.effectAllowed = "move"; }}
                      className={`rounded-md border border-border border-l-4 ${priColor(t.priority)} bg-card p-2.5 cursor-pointer hover:shadow-sm hover:border-primary/40 transition`}
                      onClick={() => onEdit(t)}
                    >
                      <div className="text-sm font-medium line-clamp-2 mb-1.5">{t.title}</div>
                      <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
                        {t.due_date ? <span className="inline-flex items-center gap-0.5"><CalendarDays className="h-3 w-3" />{fmtDate(t.due_date)}</span> : null}
                        {t.priority && t.priority !== "normal" ? <span className="inline-flex items-center gap-0.5"><Flag className="h-3 w-3" />{PRIORITY_LABEL[t.priority] ?? t.priority}</span> : null}
                        {(t.tags ?? []).slice(0, 2).map((tag) => <span key={tag} className="px-1.5 py-0 rounded-full bg-muted">{tag}</span>)}
                      </div>
                      {chips.length > 0 ? (
                        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                          {chips.map((c, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                              {c.name}: <b>{c.value}</b>
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              <InlineAddRow
                placeholder="Thêm việc…"
                onSubmit={(title) => quickCreateInline(title, c.key)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}



/* ----- Task Timeline (Gantt) ----- */
function TaskTimelineView({ tasks, onEdit }: { tasks: Task[]; onEdit: (t: Task) => void }) {
  const dated = tasks.filter((t) => t.start_date || t.due_date);
  // Luôn render khung tháng hiện tại; nếu có task ngoài tháng thì mở rộng range bao trùm.
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const dates = dated.flatMap((t) => [t.start_date, t.due_date].filter(Boolean) as string[]).map((d) => new Date(d));
  const minD = new Date(Math.min(monthStart.getTime(), ...dates.map((d) => d.getTime())));
  const maxD = new Date(Math.max(monthEnd.getTime(), ...dates.map((d) => d.getTime())));
  minD.setDate(minD.getDate() - 2);
  maxD.setDate(maxD.getDate() + 2);
  const dayMs = 86400000;
  const totalDays = Math.max(1, Math.round((maxD.getTime() - minD.getTime()) / dayMs));
  const dayW = 36; const labelW = 200;
  const days: Date[] = [];
  for (let i = 0; i <= totalDays; i++) { const d = new Date(minD); d.setDate(d.getDate() + i); days.push(d); }
  const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const todayIso = isoOf(today);
  return (
    <div className="overflow-auto p-4">
      {dated.length === 0 ? (
        <div className="mb-3 text-xs text-muted-foreground bg-muted/40 rounded px-3 py-2">
          Chưa có task nào có ngày bắt đầu/kết thúc — đang hiển thị khung tháng {today.getMonth()+1}/{today.getFullYear()}.
        </div>
      ) : null}
      <div className="relative" style={{ width: labelW + days.length * dayW }}>
        <div className="flex sticky top-0 bg-background z-10 border-b border-border/60">
          <div style={{ width: labelW }} className="text-xs font-bold p-2">Task</div>
          {days.map((d, i) => {
            const isMonthStart = d.getDate() === 1 || i === 0;
            const isToday = isoOf(d) === todayIso;
            return (
              <div key={i} style={{ width: dayW }} className={`text-center text-[10px] py-1 border-l border-border/40 ${isToday ? "bg-primary/10" : ""}`}>
                {isMonthStart && <div className="font-bold text-foreground">{d.getMonth() + 1}/{d.getFullYear()}</div>}
                <div className={isToday ? "text-primary font-bold" : "text-muted-foreground"}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {dated.length === 0 ? (
          <div className="h-24 flex items-center justify-center text-xs text-muted-foreground">
            Thêm ngày bắt đầu/kết thúc cho task để hiện thanh tiến độ ở đây.
          </div>
        ) : dated.map((t) => {
          const start = t.start_date ? new Date(t.start_date) : t.due_date ? new Date(t.due_date) : minD;
          const end = t.due_date ? new Date(t.due_date) : start;
          const offsetDays = Math.round((start.getTime() - minD.getTime()) / dayMs);
          const spanDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / dayMs) + 1);
          return (
            <div key={t.id} className="flex items-center border-b border-border/40 h-10 hover:bg-accent/30">
              <div style={{ width: labelW }} className="px-2 text-sm truncate cursor-pointer" onClick={() => onEdit(t)}>{t.title}</div>
              <div className="relative flex-1 h-full">
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-6 rounded-md cursor-pointer hover:opacity-80 flex items-center px-2 text-[10px] text-white font-medium truncate"
                  style={{
                    left: offsetDays * dayW,
                    width: spanDays * dayW - 4,
                    background: t.status === "done" ? "#10b981" : t.priority === "urgent" ? "#ef4444" : t.priority === "high" ? "#f97316" : "#6366f1",
                  }}
                  onClick={() => onEdit(t)}
                >{t.title}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ----- Task Calendar (month) ----- */
function TaskCalendarView({ tasks, onEdit, onNew }: { tasks: Task[]; onEdit: (t: Task) => void; onNew: (dateIso: string) => void }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const year = cursor.getFullYear(); const month = cursor.getMonth();
  const firstWeekday = (cursor.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const byDate = useMemo(() => {
    const m = new Map<string, Task[]>();
    for (const t of tasks) { if (!t.due_date) continue; const arr = m.get(t.due_date) ?? []; arr.push(t); m.set(t.due_date, arr); }
    return m;
  }, [tasks]);
  function iso(d: Date) {
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  const todayIso = iso(new Date());
  const prColor: Record<string, string> = {
    urgent: "bg-red-500/20 text-red-200", high: "bg-orange-500/20 text-orange-200",
    normal: "bg-indigo-500/20 text-indigo-200", low: "bg-slate-500/20 text-slate-200",
  };
  return (
    <div className="p-2 md:p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-base md:text-lg font-bold">{cursor.toLocaleString("vi-VN", { month: "long", year: "numeric" })}</div>
        <div className="flex gap-1">
          <GhostBtn onClick={() => setCursor(new Date(year, month - 1, 1))}><ChevronLeft className="h-4 w-4" /></GhostBtn>
          <GhostBtn onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }}>Hôm nay</GhostBtn>
          <GhostBtn onClick={() => setCursor(new Date(year, month + 1, 1))}><ChevronRight className="h-4 w-4" /></GhostBtn>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-7 text-xs font-bold text-muted-foreground border-b border-border/60">
            {["T2","T3","T4","T5","T6","T7","CN"].map((w) => <div key={w} className="p-2 text-center">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 border-l border-t border-border/40">
            {cells.map((d, i) => {
              const dateIso = d ? iso(d) : "";
              const list = d ? byDate.get(dateIso) ?? [] : [];
              const isToday = dateIso === todayIso;
              return (
                <div key={i} className={`min-h-20 md:min-h-24 border-r border-b border-border/40 p-1 group ${!d ? "bg-muted/20" : ""} ${isToday ? "bg-primary/5" : ""}`}>
                  {d && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isToday ? "font-bold text-primary" : "text-muted-foreground"}`}>{d.getDate()}</span>
                        <button onClick={() => onNew(dateIso)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        {list.slice(0, 3).map((t) => (
                          <button key={t.id} onClick={() => onEdit(t)}
                            className={`block w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate ${prColor[t.priority || "normal"] || prColor.normal} ${t.status === "done" ? "opacity-50 line-through" : ""}`}>
                            {t.title}
                          </button>
                        ))}
                        {list.length > 3 && <div className="text-[10px] text-muted-foreground px-1.5">+{list.length - 3}</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}




/* ----- TaskDetail: read-only modal, click vào task → mở; có nút "Sửa" + auto-archive (sample+share) ----- */
function TaskDetail({ task, customerMap, sections, fieldByKey, projects, currentProject, groupKind, projectId, onClose, onEdit, onDelete }: {
  task: Task;
  customerMap: Map<string, string>;
  sections: TaskSection[];
  fieldByKey: Map<string, CustomField>;
  projects: Project[];
  currentProject: Project | null;
  groupKind: GroupKind;
  projectId: string;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const inv = useQuery({ queryKey: ["inventory"], queryFn: () => api.get<InventoryItem[]>("/inventory") });
  const sec = sections.find((s) => s.id === task.section_id);
  // V4 — Lấy đầy đủ customer (real_name, bank_transfer_names, vip_score, tags)
  // để Task detail hiển thị thông tin mở rộng. Optional — không có thì ẩn.
  const custFull = useQuery({
    queryKey: ["customer", task.customer_id],
    enabled: !!task.customer_id,
    queryFn: () => api.get<Customer>(`/customers/${task.customer_id}`),
  });
  const cv = (task.custom_values ?? {}) as Record<string, unknown>;
  const itemIds: string[] = Array.isArray(task.inventory_item_ids) && task.inventory_item_ids.length
    ? (task.inventory_item_ids as string[])
    : Array.isArray((cv as any)[RENTAL_ITEMS_KEY]) ? ((cv as any)[RENTAL_ITEMS_KEY] as string[]) : [];
  const items = (inv.data ?? []).filter((i) => itemIds.includes(i.id));
  const custName = task.customer_id ? customerMap.get(task.customer_id) ?? "—" : "—";

  // Số tiền: ưu tiên cột mới, fallback custom_values cũ.
  const tAny = task as any;
  const total = Number(tAny.total_amount ?? cv.total_amount ?? cv.price ?? 0) || 0;
  const deposit = Number(tAny.deposit_amount ?? cv.deposit ?? 0) || 0;
  const remaining = Number(tAny.remaining_amount ?? (total > 0 ? Math.max(0, total - deposit) : 0)) || 0;
  const fmt = (n: number) => n > 0 ? n.toLocaleString("vi-VN") + "₫" : "—";

  const [sampleShot, setSampleShot] = useState<boolean>(!!tAny.sample_shot);
  const [shared, setShared]         = useState<boolean>(!!tAny.shared);
  const [paymentReceived, setPaymentReceived] = useState<boolean>(!!tAny.payment_received);
  const [archiving, setArchiving]   = useState(false);
  useEffect(() => {
    setSampleShot(!!tAny.sample_shot);
    setShared(!!tAny.shared);
    setPaymentReceived(!!tAny.payment_received);
  }, [task.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const archiveTargetKey = useMemo(() => {
    if (groupKind === "cho-thue") return "archive-rental";
    return "archive-making"; // lam-do / chup-anh / order
  }, [groupKind]);

  const toggleFlag = async (field: "sample_shot" | "shared" | "payment_received", value: boolean) => {
    if (field === "sample_shot") setSampleShot(value);
    else if (field === "shared") setShared(value);
    else setPaymentReceived(value);
    try {
      const nextSample  = field === "sample_shot"      ? value : sampleShot;
      const nextShared  = field === "shared"           ? value : shared;
      const nextPaid    = field === "payment_received" ? value : paymentReceived;
      const allDone = nextSample && nextShared && nextPaid;
      const body: Record<string, unknown> = { [field]: value };
      // Auto-archive khi cả 3 đều ✓ và task chưa lưu trữ + project hiện tại không phải Lưu trữ.
      const isInArchive = currentProject?.project_group === "luu-tru";
      if (allDone && !tAny.archived_at && !isInArchive) {
        const target = projects.find((p) => p.key === archiveTargetKey);
        if (target) {
          setArchiving(true);
          body.archived_at = new Date().toISOString();
          body.project_id = target.id;
        }
      }
      await api.put(`/tasks/${task.id}`, body);

      // Backend `sync_task_finance` đã tự upsert dòng "Cọc" và "Thu cuối" độc lập
      // dựa trên payment_received/archived_at/completed_at — KHÔNG yêu cầu cả 3 tick.
      // Chỉ cần tick "Đã nhận đủ tiền" là dòng thu cuối xuất hiện ngay trong Tài chính.
      if (field === "payment_received") {
        qc.invalidateQueries({ queryKey: ["finance"] });
        qc.invalidateQueries({ queryKey: ["finance_categories"] });
        qc.invalidateQueries({ queryKey: ["finance_task_titles"] });
        // Cưỡng bức refetch active queries ngay, không chờ user mở lại trang.
        qc.refetchQueries({ queryKey: ["finance"], type: "active" });
      }

      qc.invalidateQueries({ queryKey: ["tasks", projectId] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      if (body.project_id) {
        // Đã chuyển project → đóng modal cho rõ ràng.
        setTimeout(() => onClose(), 250);
      }
    } catch (e) {
      // Revert UI nếu lỗi.
      if (field === "sample_shot") setSampleShot(!value);
      else if (field === "shared") setShared(!value);
      else setPaymentReceived(!value);
      alert(getErrorMessage(e, "Cập nhật thất bại"));
    } finally {
      setArchiving(false);
    }
  };

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[120px_1fr] gap-3 py-1.5 text-sm border-b border-border/30 last:border-0">
      <div className="text-xs text-muted-foreground pt-0.5">{label}</div>
      <div className="min-w-0 break-words">{children}</div>
    </div>
  );

  const extraFields = Object.entries(cv).filter(([k]) =>
    k !== RENTAL_ITEMS_KEY && k !== CHECKLIST_KEY && k !== "price"
    && k !== "total_amount" && k !== "deposit" && k !== "remaining"
    && k !== "character" && k !== "location" && k !== "order_link" && k !== "order_description"
  );

  return (
    <Modal title={task.title || "(Không tiêu đề)"} onClose={onClose}>
      <div className="space-y-1">
        {tAny.archived_at ? (
          <div className="mb-2 rounded border border-slate-500/40 bg-slate-500/10 px-2 py-1.5 text-xs">
            📦 Đã lưu trữ lúc {String(tAny.archived_at).slice(0, 10)}
          </div>
        ) : null}
        <Row label="Trạng thái">
          <span className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${task.status === "done" ? "bg-emerald-500" : task.status === "in_progress" ? "bg-blue-500" : task.status === "blocked" ? "bg-rose-500" : "bg-muted-foreground/40"}`} />
            {TASK_STATUS_LABEL[task.status ?? ""] ?? task.status ?? "—"}
          </span>
        </Row>
        <Row label="Ưu tiên">
          {task.priority ? <span>{PRIORITY_LABEL[task.priority] ?? task.priority}</span> : "—"}
        </Row>
        {sec ? <Row label="Section">{sec.name}</Row> : null}
        <Row label="Khách hàng">
          {(() => {
            const cf: any = custFull.data;
            const vip = Number(cf?.vip_score ?? 0);
            const ctags: string[] = Array.isArray(cf?.tags) ? cf.tags.filter(Boolean) : [];
            const cReal: string | null = typeof cf?.real_name === "string" && cf.real_name.trim() ? cf.real_name.trim() : null;
            const cBank: string[] = Array.isArray(cf?.bank_transfer_names)
              ? cf.bank_transfer_names.map((s: unknown) => (typeof s === "string" ? s.trim() : "")).filter(Boolean)
              : [];
            return (
              <div className="space-y-0.5">
                <div className="flex flex-wrap items-center gap-1">
                  <span>{custName}</span>
                  {vip > 0 ? (
                    <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium ${vip >= 80 ? "bg-amber-500/15 text-amber-600" : vip >= 50 ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground"}`}>VIP {vip}</span>
                  ) : null}
                  {ctags.slice(0, 4).map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">{t}</span>
                  ))}
                  {ctags.length > 4 ? <span className="text-[10px] text-muted-foreground/70">+{ctags.length - 4}</span> : null}
                </div>
                {cReal ? (
                  <div className="text-[11px] italic text-muted-foreground/80">Tên thật: {cReal}</div>
                ) : null}
                {cBank.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1">
                    {cBank.slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20" title={`Tên CK: ${t}`}>₫ {t}</span>
                    ))}
                    {cBank.length > 4 ? <span className="text-[10px] text-muted-foreground/70">+{cBank.length - 4}</span> : null}
                  </div>
                ) : null}
              </div>
            );
          })()}
        </Row>
        {tAny.customer_quick_name ? <Row label="Khách (nhanh)">{String(tAny.customer_quick_name)}</Row> : null}
        {tAny.contact_link ? (
          <Row label="Liên hệ"><a href={String(tAny.contact_link)} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{String(tAny.contact_link)}</a></Row>
        ) : null}
        <Row label="Từ ngày">{task.start_date || "—"}</Row>
        <Row label="Đến ngày">{task.due_date || "—"}</Row>
        {tAny.return_date ? <Row label="Ngày trả">{String(tAny.return_date)}</Row> : null}
        {tAny.shoot_time ? <Row label="Giờ chụp">{String(tAny.shoot_time)}</Row> : null}
        {tAny.character_name || cv.character ? <Row label="Nhân vật">{String(tAny.character_name ?? cv.character)}</Row> : null}
        {tAny.concept ? <Row label="Concept">{String(tAny.concept)}</Row> : null}
        {tAny.location || cv.location ? <Row label="Địa điểm">{String(tAny.location ?? cv.location)}</Row> : null}
        {(task.tags ?? []).length ? (
          <Row label="Tags">
            <div className="flex flex-wrap gap-1">
              {(task.tags ?? []).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{t}</span>)}
            </div>
          </Row>
        ) : null}
        {items.length ? (
          <Row label="Vật phẩm">
            <div className="flex flex-wrap gap-1">
              {items.map((i) => <span key={i.id} className="text-xs px-1.5 py-0.5 rounded bg-muted">{i.name}{i.variant ? ` · ${i.variant}` : ""}</span>)}
            </div>
          </Row>
        ) : null}
        {total > 0 || deposit > 0 ? (
          <Row label="Tiền">
            <span className="inline-flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
              <span>Tổng: <b>{fmt(total)}</b></span>
              <span>Cọc: <b>{fmt(deposit)}</b></span>
              <span>Còn lại: <b className="text-primary">{fmt(remaining)}</b></span>
            </span>
          </Row>
        ) : null}
        {task.description ? <Row label="Chi tiết"><div className="whitespace-pre-wrap">{task.description}</div></Row> : null}
        {cv.order_link ? (
          <Row label="Link sản phẩm"><a href={String(cv.order_link)} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{String(cv.order_link)}</a></Row>
        ) : null}
        {cv.order_description ? <Row label="Mô tả order"><div className="whitespace-pre-wrap">{String(cv.order_description)}</div></Row> : null}

        {/* Khối Piwigo — link thủ công + nút Mở album */}
        {(task.piwigo_album_url || task.piwigo_album_id) ? (
          <Row label="Album Piwigo">
            <div className="flex flex-wrap items-center gap-2">
              {task.piwigo_album_url ? (
                <a href={task.piwigo_album_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20">
                  ↗ Mở album
                </a>
              ) : null}
              {task.piwigo_album_url ? <span className="text-[11px] text-muted-foreground break-all">{task.piwigo_album_url}</span> : null}
              {task.album_status && task.album_status !== "none" ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{task.album_status}</span>
              ) : null}
            </div>
          </Row>
        ) : null}

        {/* Sample + Share → auto-archive khi cả 2 ✓ */}
        {currentProject?.project_group !== "luu-tru" ? (
          <Row label="Hoàn tất">
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sampleShot} disabled={archiving} onChange={(e) => toggleFlag("sample_shot", e.target.checked)} />
                Đã chụp sample
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={shared} disabled={archiving} onChange={(e) => toggleFlag("shared", e.target.checked)} />
                Đã share
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={paymentReceived} disabled={archiving} onChange={(e) => toggleFlag("payment_received", e.target.checked)} />
                Đã nhận đủ tiền
                {remaining > 0 ? <span className="text-[11px] text-muted-foreground">(còn {fmt(remaining)})</span> : null}
              </label>
              <div className="text-[11px] text-muted-foreground">Tick cả 3 → task tự chuyển sang nhóm Lưu trữ. Tick "Đã nhận đủ tiền" sẽ tự ghi khoản còn lại vào Tài chính.</div>
            </div>
          </Row>
        ) : null}

        {extraFields.map(([k, v]) => {
          const f = fieldByKey.get(k);
          if (v == null || v === "") return null;
          return <Row key={k} label={f?.name ?? k}>{typeof v === "boolean" ? (v ? "✓" : "—") : String(v)}</Row>;
        })}
      </div>
      <div className="flex justify-between items-center gap-2 mt-4 pt-3 border-t border-border">
        <button onClick={onDelete} className="text-xs text-destructive hover:underline inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" />Xoá</button>
        <div className="flex gap-2">
          <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
          <PrimaryBtn type="button" onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1 inline" />Sửa</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function TaskForm({ projectId, groupKind = "cho-thue", initial, customers, sections, fields, onClose, onSaved }: {
  projectId: string; groupKind?: GroupKind; initial: Task | null; customers: Customer[];
  sections?: TaskSection[]; fields?: CustomField[];
  onClose: () => void; onSaved: () => void;
}) {
  // Cờ render theo loại project — mỗi nhóm có barem nhập liệu khác nhau.
  const isChoThue = groupKind === "cho-thue";
  const isLamDo = groupKind === "lam-do";
  const isChupAnh = groupKind === "chup-anh";
  const isLuuTru = groupKind === "luu-tru";
  const isOrder = groupKind === "order";

  const [sectionId, setSectionId] = useState<string | null>(initial?.section_id ?? null);
  const [customValues, setCustomValues] = useState<Record<string, unknown>>(() => ({ ...(initial?.custom_values ?? {}) }));
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  // Status mặc định khi tạo mới: theo cột đang xem (section.key todo/doing/done) — nếu không có thì 'todo'.
  const initialStatus = useMemo(() => {
    if (initial?.status) return initial.status;
    if (initial?.section_id && sections) {
      const s = sections.find((x) => x.id === initial.section_id);
      const map: Record<string, string> = { todo: "todo", doing: "in_progress", done: "done" };
      if (s?.key && map[s.key]) return map[s.key];
    }
    return "todo";
  }, [initial, sections]);
  const [status, setStatus] = useState(initialStatus);
  const [priority, setPriority] = useState(initial?.priority ?? "normal");
  const [customerId, setCustomerId] = useState<string | null>(initial?.customer_id ?? null);
  const [startDate, setStartDate] = useState(initial?.start_date ?? "");
  const [dueDate, setDueDate] = useState(initial?.due_date ?? "");
  const [reminderDays, setReminderDays] = useState<string>(
    initial?.reminder_days_before != null ? String(initial.reminder_days_before) : "",
  );
  const [tagList, setTagList] = useState<string[]>(initial?.tags ?? []);
  const [tagDraft, setTagDraft] = useState("");
  const [rentalItems, setRentalItems] = useState<string[]>(() => {
    if (Array.isArray(initial?.inventory_item_ids) && initial.inventory_item_ids.length) return initial.inventory_item_ids as string[];
    const v = (initial?.custom_values as any)?.[RENTAL_ITEMS_KEY];
    return Array.isArray(v) ? (v as string[]) : [];
  });
  // Phase R1 — region override per-rental (lưu vào custom_values.__rental_region).
  // Mặc định lấy theo customer.region; user có thể ép HCM/Tỉnh cho đơn này.
  const [rentalRegion, setRentalRegion] = useState<"" | "hcm" | "tinh">(
    ((initial?.custom_values as any)?.__rental_region as "" | "hcm" | "tinh") ?? ""
  );
  // Vùng hiệu lực (override > customer.region > 'hcm') — dùng cho auto-calc ship/return.
  const effectiveRegion = useMemo<"hcm" | "tinh">(() => {
    if (rentalRegion) return rentalRegion;
    const c = customers.find((x) => x.id === customerId);
    return ((c?.region as "hcm" | "tinh") ?? "hcm");
  }, [rentalRegion, customers, customerId]);
  // Ngày thuê (event date). Auto-derive start_date (ship) và due_date (nhận về) theo vùng.
  const [rentalDate, setRentalDate] = useState<string>(() => {
    const cv = (initial?.custom_values as any) ?? {};
    if (cv.rental_date) return String(cv.rental_date);
    // Fallback: nếu task cũ chỉ có start_date → suy ngược ngày thuê = start_date + buffer mặc định HCM(1).
    if (initial?.start_date) return addDaysISO(String(initial.start_date), 1);
    return "";
  });
  const shiftDateStr = (d: string, days: number) => (d ? addDaysISO(d, days) : "");
  const applyRentalDate = (d: string, region: "hcm" | "tinh") => {
    setRentalDate(d);
    if (!d) return;
    const buf = region === "tinh" ? 3 : 1;
    setStartDate(shiftDateStr(d, -buf));
    setDueDate(shiftDateStr(d, buf));
  };
  // Khi đổi vùng (override hoặc khách HCM↔Tỉnh) mà đã có rentalDate → recompute lại ship/return.
  // Bỏ qua nếu user vừa override thủ công start/due (đánh dấu qua datesManuallyEdited).
  const [datesManuallyEdited, setDatesManuallyEdited] = useState(false);
  useEffect(() => {
    if (!rentalDate || datesManuallyEdited) return;
    const buf = effectiveRegion === "tinh" ? 3 : 1;
    setStartDate(shiftDateStr(rentalDate, -buf));
    setDueDate(shiftDateStr(rentalDate, buf));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRegion]);

  // Sync contact của khách (Facebook / SĐT / Instagram) qua TaskForm → cập nhật ngược về customers.
  const [custFb, setCustFb] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [custIg, setCustIg] = useState("");
  const [custContactDirty, setCustContactDirty] = useState(false);
  useEffect(() => {
    const c = customers.find((x) => x.id === customerId);
    setCustFb(c?.facebook ?? "");
    setCustPhone(c?.phone ?? "");
    setCustIg(c?.instagram ?? "");
    setCustContactDirty(false);
  }, [customerId, customers]);
  const qcTask = useQueryClient();
  const [productIds, setProductIds] = useState<string[]>(() =>
    Array.isArray(initial?.product_ids) ? (initial?.product_ids as string[]) : []
  );
  const [albumUrl, setAlbumUrl] = useState(initial?.piwigo_album_url ?? "");
  const [albumId, setAlbumId] = useState(initial?.piwigo_album_id != null ? String(initial.piwigo_album_id) : "");
  const [albumStatus, setAlbumStatus] = useState<string>(initial?.album_status ?? "none");

  // New money / specialized fields (lưu vào custom_values với key chuẩn).
  const [totalAmount, setTotalAmount] = useState<string>(() => {
    const cv = (initial?.custom_values as any) ?? {};
    if (cv.total_amount != null) return String(cv.total_amount);
    if (cv.price != null) return String(cv.price);
    return "";
  });
  const [deposit, setDeposit] = useState<string>(() => {
    const cv = (initial?.custom_values as any) ?? {};
    return cv.deposit != null ? String(cv.deposit) : "";
  });
  const [character, setCharacter] = useState<string>(() => String((initial?.custom_values as any)?.character ?? ""));
  const [location, setLocation] = useState<string>(() => String((initial?.custom_values as any)?.location ?? ""));
  const [orderLink, setOrderLink] = useState<string>(() => String((initial?.custom_values as any)?.order_link ?? ""));
  const [orderDesc, setOrderDesc] = useState<string>(() => String((initial?.custom_values as any)?.order_description ?? ""));
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [err, setErr] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>(() => {
    const v = (initial?.custom_values as any)?.[CHECKLIST_KEY];
    return Array.isArray(v) ? v as ChecklistItem[] : [];
  });
  const defaults = useQuery({
    queryKey: ["photo_checklist_defaults"],
    queryFn: () => api.get<PhotoChecklistDefault[]>("/photo-checklist-defaults"),
  });
  const inventoryAll = useQuery({ queryKey: ["inventory"], queryFn: () => api.get<InventoryItem[]>("/inventory") });

  // Auto-title: "{tên khách} – {tên vật phẩm 1[, +N]}"
  const autoTitle = useMemo(() => {
    const cust = customers.find((c) => c.id === customerId);
    const custName = cust?.name?.trim() || "";
    const items = (inventoryAll.data ?? []).filter((i) => rentalItems.includes(i.id));
    const firstItem = items[0] ? `${items[0].name}${items[0].variant ? ` ${items[0].variant}` : ""}` : "";
    const more = items.length > 1 ? ` +${items.length - 1}` : "";
    const itemPart = firstItem ? `${firstItem}${more}` : "";
    // Per groupKind: lam-do/order dùng description ngắn; chup-anh dùng character.
    let tail = itemPart;
    if (!tail && (isLamDo || isOrder) && description) tail = description.split("\n")[0].slice(0, 40);
    if (!tail && isChupAnh && character) tail = character;
    if (tail) return capitalizeFirstVN(tail);
    if (custName) return custName;
    return "";
  }, [customers, customerId, inventoryAll.data, rentalItems, description, character, isLamDo, isOrder, isChupAnh]);

  const allTasks = useQuery({
    queryKey: ["tasks", projectId, "all-for-suggest"],
    queryFn: () => api.get<Task[]>("/tasks", { project_id: projectId }),
    enabled: !!projectId,
  });
  const titleSuggestions = useMemo(
    () => Array.from(new Set((allTasks.data ?? []).map((t) => t.title).filter(Boolean))),
    [allTasks.data],
  );
  const tagSuggestions = useMemo(() => {
    const s = new Set<string>();
    for (const t of allTasks.data ?? []) for (const tag of t.tags ?? []) if (tag) s.add(tag);
    return Array.from(s);
  }, [allTasks.data]);

  // Auto-archive: tất cả checklist done → status='done'.
  useEffect(() => {
    if (checklist.length > 0 && checklist.every((c) => c.checked) && status !== "done") {
      setStatus("done");
    }
  }, [checklist]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cảnh báo trùng lịch.
  const conflicts = useMemo(() => {
    if (rentalItems.length === 0) return [] as { itemId: string; itemName: string; taskTitle: string; range: string }[];
    // Phase R1 — mở rộng cửa sổ giữ đồ theo vùng (HCM 1+1 ngày, Tỉnh 3+3 ngày).
    const custMap = new Map(customers.map((c) => [c.id, c]));
    const regionDays = (r: "hcm" | "tinh") => (r === "tinh" ? 3 : 1);
    const shiftDate = (d: string | null, days: number) => (d ? addDaysISO(d, days) : null);
    const windowFor = (s: string | null, e: string | null, region: "hcm" | "tinh") => {
      const buf = regionDays(region);
      return [shiftDate(s, -buf), shiftDate(e || s, buf)] as [string | null, string | null];
    };
    // Vùng của task hiện tại: override > customer > default 'hcm'.
    const currentRegion: "hcm" | "tinh" = rentalRegion
      ? rentalRegion
      : ((custMap.get(customerId ?? "")?.region as "hcm" | "tinh") ?? "hcm");
    const [a1, a2] = windowFor(startDate || null, dueDate || null, currentRegion);
    const overlap = (b1: string | null, b2: string | null) => {
      const A1 = a1 || "0000-01-01"; const A2 = a2 || "9999-12-31";
      const B1 = b1 || "0000-01-01"; const B2 = b2 || b1 || "9999-12-31";
      return A1 <= B2 && B1 <= A2;
    };
    const invMap = new Map((inventoryAll.data ?? []).map((i) => [i.id, i]));
    const out: { itemId: string; itemName: string; taskTitle: string; range: string }[] = [];
    for (const t of allTasks.data ?? []) {
      if (initial && t.id === initial.id) continue;
      if (t.status === "done" || t.status === "cancelled") continue;
      const ids = (t.inventory_item_ids ?? []) as string[];
      const dup = ids.filter((id) => rentalItems.includes(id));
      if (dup.length === 0) continue;
      // Cửa sổ giữ đồ của task kia cũng được mở rộng theo region của khách kia.
      const otherCv = (t.custom_values ?? {}) as Record<string, unknown>;
      const otherOverride = otherCv.__rental_region as "hcm" | "tinh" | undefined;
      const otherRegion: "hcm" | "tinh" = otherOverride
        ? otherOverride
        : ((custMap.get(t.customer_id ?? "")?.region as "hcm" | "tinh") ?? "hcm");
      const [b1, b2] = windowFor(t.start_date ?? null, t.due_date ?? null, otherRegion);
      if (!overlap(b1, b2)) continue;
      const range = `${t.start_date ?? "?"} → ${t.due_date ?? "?"} (${otherRegion === "tinh" ? "Tỉnh" : "HCM"})`;
      for (const id of dup) {
        const item = invMap.get(id);
        out.push({ itemId: id, itemName: item ? `${item.name}${item.variant ? ` · ${item.variant}` : ""}` : id, taskTitle: t.title, range });
      }
    }
    return out;
  }, [rentalItems, startDate, dueDate, allTasks.data, inventoryAll.data, initial, customers, customerId, rentalRegion]);

  // Số "Còn lại" auto = Tổng - Cọc (chỉ để hiển thị).
  const totalN = parseAmount(totalAmount);
  const depositN = parseAmount(deposit);
  const remainingN = totalN > 0 ? Math.max(0, totalN - depositN) : 0;

  const save = useMutation({
    mutationFn: async () => {
      const custom = { ...(initial?.custom_values ?? {}) } as Record<string, unknown>;

      // Tiền: tổng / cọc / còn lại (lưu canonical keys mà list view đọc)
      if (totalN > 0) custom.total_amount = totalN; else delete custom.total_amount;
      if (depositN > 0) custom.deposit = depositN; else delete custom.deposit;
      if (totalN > 0) custom.remaining = remainingN; else delete custom.remaining;
      // Giữ legacy `price` để backward-compat (mirror total_amount)
      if (totalN > 0) custom.price = totalN; else delete custom.price;

      if (character.trim()) custom.character = character.trim(); else delete custom.character;
      if (location.trim()) custom.location = location.trim(); else delete custom.location;
      if (orderLink.trim()) custom.order_link = orderLink.trim(); else delete custom.order_link;
      if (orderDesc.trim()) custom.order_description = orderDesc.trim(); else delete custom.order_description;

      if (checklist.length > 0) custom[CHECKLIST_KEY] = checklist; else delete custom[CHECKLIST_KEY];
      if (rentalItems.length > 0) custom[RENTAL_ITEMS_KEY] = rentalItems; else delete custom[RENTAL_ITEMS_KEY];
      if (rentalRegion) custom.__rental_region = rentalRegion; else delete (custom as any).__rental_region;
      if (rentalDate) custom.rental_date = rentalDate; else delete (custom as any).rental_date;
      for (const [k, v] of Object.entries(customValues)) {
        if (v === "" || v == null) delete custom[k]; else custom[k] = v;
      }

      const tagArr = [...tagList, ...tagDraft.split(",").map((t) => t.trim()).filter(Boolean)];
      const body: Record<string, unknown> = {
        project_id: projectId,
        title: (capitalizeFirstVN(title.trim()) || autoTitle || "(Việc mới)"),
        description: description || null,
        status,
        priority,
        section_id: sectionId ?? (() => {
          // Auto-suy section theo status nếu chưa chọn → tránh task rơi "Chưa phân nhóm".
          const map: Record<string, string> = { todo: "todo", in_progress: "doing", done: "done" };
          const wantedKey = map[status];
          return wantedKey ? (((sections ?? []).find((s) => s.key === wantedKey)?.id) ?? null) : null;
        })(),
        customer_id: customerId || null,
        start_date: startDate || null,
        due_date: dueDate || null,
        reminder_days_before: reminderDays === "" ? null : Number(reminderDays),
        tags: tagArr,
        piwigo_album_url: albumUrl || null,
        piwigo_album_id: albumId ? Number(albumId) : null,
        album_status: albumStatus && albumStatus !== "none" ? albumStatus : (albumUrl ? "manual" : null),
        custom_values: custom,
        product_ids: productIds,
        inventory_item_ids: rentalItems,
        // Tiền: lưu cột riêng (B0 đã thêm column) — đồng thời giữ mirror trong custom_values cho List view cũ.
        total_amount: totalN,
        deposit_amount: depositN,
        remaining_amount: remainingN,
        completed_at: status === "done"
          ? (initial?.completed_at ?? new Date().toISOString())
          : null,
      };
      // Guard: bỏ undefined, không cho rớt key custom_values; chỉ cho phép gửi date = null
      // (xóa ngày) khi user thực sự ở trong form edit này (mặc định cho phép).
      const safeBody = sanitizeTaskPayload(body);
      if (initial?.id) return api.put(`/tasks/${initial.id}`, safeBody);
      return api.post("/tasks", safeBody);
    },
    onSuccess: async () => {
      try {
        if (title.trim()) pushFieldHistory("title", title.trim());
        if (character.trim()) pushFieldHistory("custom:character", character.trim());
        if (location.trim()) pushFieldHistory("custom:location", location.trim());
        for (const f of fields ?? []) {
          if (f.type && f.type !== "text") continue;
          const k = f.key || f.id;
          const v = customValues[k];
          if (typeof v === "string" && v.trim()) pushFieldHistory("custom:" + k, v.trim());
        }
      } catch { /* ignore */ }
      // Sync ngược thông tin liên hệ về customer (Facebook / SĐT / Instagram).
      if (customerId && custContactDirty) {
        try {
          await api.put(`/customers/${customerId}`, {
            facebook: custFb.trim() || null,
            phone: custPhone.trim() || null,
            instagram: custIg.trim() || null,
          });
          qcTask.invalidateQueries({ queryKey: ["customers"] });
        } catch { /* ignore */ }
      }
      if (status === "done" && rentalItems.length > 0) {
        try {
          await Promise.all(rentalItems.map((id) => api.put(`/inventory/${id}`, { available: true })));
        } catch { /* ignore */ }
      }
      onSaved();
    },
    onError: (e) => setErr(getErrorMessage(e, "Lưu thất bại")),
  });

  /* ---- Sub-blocks dùng chung ---- */
  const customerBlock = (
    <label className="block text-sm">Khách hàng <span className="text-destructive">*</span>
      <div className="mt-1">
        <CustomerSearchPicker value={customerId} onChange={(id) => setCustomerId(id)} />
      </div>
      {customerId ? (
        <div className="mt-2 space-y-1.5">
          <CustomerContactIcons cust={customers.find((c) => c.id === customerId) ?? null} />
          <div className="grid gap-1.5 sm:grid-cols-3 rounded border border-dashed border-border bg-muted/20 p-2">
            <label className="block text-[11px] text-muted-foreground">Facebook khách
              <Input value={custFb} onChange={(e) => { setCustFb(e.target.value); setCustContactDirty(true); }}
                placeholder="username hoặc URL" className="mt-0.5 h-8 text-xs" />
            </label>
            <label className="block text-[11px] text-muted-foreground">SĐT
              <Input value={custPhone} onChange={(e) => { setCustPhone(e.target.value); setCustContactDirty(true); }}
                placeholder="0xxx…" className="mt-0.5 h-8 text-xs" />
            </label>
            <label className="block text-[11px] text-muted-foreground">Instagram
              <Input value={custIg} onChange={(e) => { setCustIg(e.target.value); setCustContactDirty(true); }}
                placeholder="@user hoặc URL" className="mt-0.5 h-8 text-xs" />
            </label>
            <div className="sm:col-span-3 text-[10px] text-muted-foreground">
              ⓘ Sửa ở đây sẽ tự lưu vào hồ sơ khách khi bấm Lưu task.
            </div>
          </div>
        </div>
      ) : null}
    </label>
  );

  const moneyBlock = (
    <div className="grid gap-2 sm:grid-cols-3">
      <label className="block text-sm">Tổng tiền
        <Input type="text" inputMode="numeric" value={totalAmount}
          onChange={(e) => setTotalAmount(e.target.value)}
          placeholder="vd: 1.500.000 hoặc 1.5tr" className="mt-1" />
      </label>
      <label className="block text-sm">Cọc
        <Input type="text" inputMode="numeric" value={deposit}
          onChange={(e) => setDeposit(e.target.value)}
          placeholder="vd: 500k" className="mt-1" />
      </label>
      <label className="block text-sm">Còn lại <span className="text-muted-foreground text-xs">(auto)</span>
        <Input type="text" value={remainingN > 0 ? remainingN.toLocaleString("vi-VN") + "₫" : ""} disabled className="mt-1 bg-muted/40" />
      </label>
    </div>
  );

  const priorityStatusBlock = (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="block text-sm">Trạng thái
        <Select value={status ?? "todo"} onChange={(e) => setStatus(e.target.value)} className="mt-1">
          <option value="todo">Cần làm</option>
          <option value="in_progress">Đang làm</option>
          <option value="done">Đã xong</option>
          <option value="blocked">Tạm dừng</option>
        </Select>
      </label>
      <label className="block text-sm">Ưu tiên
        <Select value={priority ?? "normal"} onChange={(e) => setPriority(e.target.value)} className="mt-1">
          <option value="low">Thấp</option>
          <option value="normal">Bình thường</option>
          <option value="high">Cao</option>
          <option value="urgent">Khẩn</option>
        </Select>
      </label>
    </div>
  );

  const tagsBlock = (
    <label className="block text-sm">Tags
      <div className="mt-1 space-y-1.5">
        <div className="flex flex-wrap gap-1.5">
          {tagList.length === 0 ? (
            <span className="text-xs italic text-muted-foreground">Chưa có tag</span>
          ) : tagList.map((t, idx) => (
            <span key={t + idx} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs">
              {t}
              <button type="button" onClick={() => setTagList(tagList.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive">×</button>
            </span>
          ))}
        </div>
        <SuggestInput value={tagDraft}
          onChange={(v) => {
            if (v.endsWith(",")) {
              const t = v.slice(0, -1).trim();
              if (t && !tagList.includes(t)) setTagList([...tagList, t]);
              setTagDraft("");
            } else { setTagDraft(v); }
          }}
          suggestions={tagSuggestions.filter((t) => !tagList.includes(t))}
          placeholder="Gõ tag rồi Enter (hoặc dấu phẩy)" />
      </div>
    </label>
  );

  const titleBlock = (
    <label className="block text-sm">Yêu cầu của khách <span className="text-muted-foreground text-xs">(để trống = tự sinh)</span>
      <div className="mt-1" onBlur={() => setTitle((t) => capitalizeFirstVN(t))}>
        <SuggestInput value={title} onChange={setTitle} suggestions={titleSuggestions} placeholder={autoTitle || "VD: Wig đỏ Nezuko 80cm + váy lolita"} />
      </div>
    </label>
  );

  return (
    <Modal title={initial ? "Sửa công việc" : "Thêm công việc"} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); setErr(null); save.mutate(); }}>

        {/* ============ BAREM: CHO THUÊ ============ */}
        {isChoThue ? (
          <>
            {customerBlock}
            {(() => {
              const cust = customers.find((c) => c.id === customerId);
              const effectiveRegion: "hcm" | "tinh" = rentalRegion
                ? rentalRegion
                : ((cust?.region as "hcm" | "tinh") ?? "hcm");
              const days = effectiveRegion === "tinh" ? 7 : 3;
              return (
                <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-muted/30 px-2 py-1.5 text-xs">
                  <span className="text-muted-foreground">Vùng giữ đồ:</span>
                  <span className={`font-medium ${effectiveRegion === "tinh" ? "text-amber-600" : "text-emerald-600"}`}>
                    {effectiveRegion === "tinh" ? "Tỉnh (7 ngày, gồm vận chuyển)" : "TP.HCM (3 ngày)"}
                  </span>
                  <span className="text-muted-foreground">· danh sách khoá {days} ngày</span>
                  <span className="flex-1" />
                  <label className="flex items-center gap-1 text-muted-foreground">
                    Ép vùng:
                    <Select value={rentalRegion} onChange={(e) => setRentalRegion(e.target.value as "" | "hcm" | "tinh")} className="h-7 text-xs">
                      <option value="">Theo khách ({((cust?.region as string) ?? "hcm") === "tinh" ? "Tỉnh" : "HCM"})</option>
                      <option value="hcm">HCM</option>
                      <option value="tinh">Tỉnh</option>
                    </Select>
                  </label>
                </div>
              );
            })()}
            <div className="space-y-2">
              <label className="block text-sm">Ngày thuê <span className="text-destructive">*</span>
                <span className="ml-1 text-[11px] text-muted-foreground">
                  (tự tính Ngày ship & Ngày nhận về theo vùng {effectiveRegion === "tinh" ? "Tỉnh ±3" : "HCM ±1"})
                </span>
                <DateInputVN value={rentalDate}
                  onChange={(iso) => { setDatesManuallyEdited(false); applyRentalDate(iso, effectiveRegion); }}
                  className="mt-1" />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">Ngày ship <span className="text-muted-foreground text-xs">(auto, có thể sửa)</span>
                  <DateInputVN value={startDate ?? ""}
                    onChange={(iso) => { setStartDate(iso); setDatesManuallyEdited(true); }}
                    className="mt-1" />
                </label>
                <label className="block text-sm">Ngày nhận về <span className="text-muted-foreground text-xs">(auto, có thể sửa)</span>
                  <DateInputVN value={dueDate ?? ""}
                    onChange={(iso) => { setDueDate(iso); setDatesManuallyEdited(true); }}
                    className="mt-1" />
                </label>
              </div>
              {datesManuallyEdited && rentalDate ? (
                <button type="button"
                  onClick={() => { setDatesManuallyEdited(false); applyRentalDate(rentalDate, effectiveRegion); }}
                  className="text-[11px] text-primary hover:underline">
                  ↺ Đặt lại ship/nhận về theo Ngày thuê
                </button>
              ) : null}
            </div>
            <label className="block text-sm">Vật phẩm cho thuê (kho)
              <div className="mt-1">
                <InventoryMultiPicker value={rentalItems} onChange={setRentalItems} />
              </div>
              {conflicts.length > 0 ? (
                <div className="mt-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-1.5 text-xs text-orange-700 dark:text-orange-300 space-y-0.5">
                  <div className="font-semibold">⚠ Trùng lịch ({conflicts.length}):</div>
                  {conflicts.slice(0, 6).map((c, i) => (
                    <div key={i} className="truncate">• <b>{c.itemName}</b> — "{c.taskTitle}" ({c.range})</div>
                  ))}
                  {conflicts.length > 6 ? <div>… và {conflicts.length - 6} mục khác</div> : null}
                </div>
              ) : null}
            </label>
            {moneyBlock}
            {priorityStatusBlock}
            <label className="block text-sm">Ghi chú
              <Textarea rows={2} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} className="mt-1" placeholder="Ghi chú riêng cho đơn này…" />
            </label>
            {titleBlock}
            {tagsBlock}
          </>
        ) : null}

        {/* ============ BAREM: LÀM ĐỒ ============ */}
        {isLamDo ? (
          <>
            {customerBlock}
            <label className="block text-sm">Mô tả yêu cầu <span className="text-destructive">*</span>
              <Textarea rows={4} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} className="mt-1" placeholder="Mô tả chi tiết món cần làm: kích thước, vật liệu, màu sắc, tham khảo…" />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">Deadline (ngày giao)
                <DateInputVN value={dueDate ?? ""} onChange={(iso) => setDueDate(iso)} className="mt-1" />
              </label>
              <label className="block text-sm">Bắt đầu
                <DateInputVN value={startDate ?? ""} onChange={(iso) => setStartDate(iso)} className="mt-1" />
              </label>
            </div>
            {moneyBlock}
            {priorityStatusBlock}
            {titleBlock}
            {tagsBlock}
          </>
        ) : null}

        {/* ============ BAREM: CHỤP ẢNH ============ */}
        {isChupAnh ? (
          <>
            {customerBlock}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">Nhân vật / Concept <span className="text-destructive">*</span>
                <div className="mt-1">
                  <SuggestInput value={character} onChange={setCharacter}
                    suggestions={getFieldHistory("custom:character")}
                    placeholder="vd: Nezuko, Yor Forger…" />
                </div>
              </label>
              <label className="block text-sm">Bối cảnh / Địa điểm
                <div className="mt-1">
                  <SuggestInput value={location} onChange={setLocation}
                    suggestions={getFieldHistory("custom:location")}
                    placeholder="vd: Studio quận 1, công viên…" />
                </div>
              </label>
              <label className="block text-sm">Ngày chụp
                <DateInputVN value={startDate ?? ""} onChange={(iso) => { setStartDate(iso); if (!dueDate) setDueDate(iso); }} className="mt-1" />
              </label>
              <label className="block text-sm">Đến (nếu nhiều ngày)
                <DateInputVN value={dueDate ?? ""} onChange={(iso) => setDueDate(iso)} className="mt-1" />
              </label>
            </div>
            {moneyBlock}
            {priorityStatusBlock}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">Album Piwigo URL
                <Input type="url" value={albumUrl ?? ""} onChange={(e) => setAlbumUrl(e.target.value)}
                  placeholder="https://piwigo…/category/123" className="mt-1" />
              </label>
              <label className="block text-sm">Trạng thái album
                <Select value={albumStatus || "none"} onChange={(e) => setAlbumStatus(e.target.value)} className="mt-1">
                  <option value="none">Chưa có</option>
                  <option value="manual">Link thủ công</option>
                  <option value="created">Đã tạo</option>
                  <option value="uploaded">Đã upload</option>
                  <option value="error">Lỗi</option>
                </Select>
              </label>
            </div>
            {initial?.id ? (
              <TaskPiwigoActions
                taskId={initial.id} taskTitle={title}
                albumId={albumId} albumUrl={albumUrl ?? ""} albumStatus={albumStatus}
                onChange={(n) => { setAlbumId(n.albumId); setAlbumUrl(n.albumUrl); setAlbumStatus(n.albumStatus); }}
              />
            ) : null}
            {titleBlock}
            {tagsBlock}
            {/* Checklist phụ kiện — chỉ hiện sau khi đã có id */}
            {initial?.id ? (
              <ChecklistEditor items={checklist} onChange={setChecklist} defaults={defaults.data ?? []} />
            ) : (
              <div className="rounded border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                📋 Checklist phụ kiện sẽ xuất hiện sau khi <b>Lưu</b> thông tin khách (mở lại task để thêm/sửa).
              </div>
            )}
          </>
        ) : null}

        {/* ============ BAREM: ORDER ============ */}
        {isOrder ? (
          <>
            {customerBlock}
            <label className="block text-sm">Tên món order <span className="text-destructive">*</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Wig đỏ Nezuko 80cm" className="mt-1" />
            </label>
            <label className="block text-sm">Link sản phẩm
              <Input type="url" value={orderLink} onChange={(e) => setOrderLink(e.target.value)}
                placeholder="https://shopee.vn/… hoặc taobao…" className="mt-1" />
            </label>
            <label className="block text-sm">Mô tả chi tiết (size / màu / biến thể)
              <Textarea rows={3} value={orderDesc} onChange={(e) => setOrderDesc(e.target.value)} className="mt-1"
                placeholder="vd: size M, màu đỏ burgundy, ship hỏa tốc…" />
            </label>
            {moneyBlock}
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">Ngày dự kiến về
                <DateInputVN value={dueDate ?? ""} onChange={(iso) => setDueDate(iso)} className="mt-1" />
              </label>
              <label className="block text-sm">Ngày đặt
                <DateInputVN value={startDate ?? ""} onChange={(iso) => setStartDate(iso)} className="mt-1" />
              </label>
            </div>
            {priorityStatusBlock}
            {tagsBlock}
          </>
        ) : null}

        {/* ============ BAREM: LƯU TRỮ ============ */}
        {isLuuTru ? (
          <>
            <label className="block text-sm">Yêu cầu của khách
              <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => setTitle((t) => capitalizeFirstVN(t))} placeholder="vd: Bộ kimono Nezuko (đã hoàn tất)" className="mt-1" />
            </label>
            <label className="block text-sm">Ghi chú lưu trữ
              <Textarea rows={3} value={description ?? ""} onChange={(e) => setDescription(e.target.value)} className="mt-1" placeholder="Vị trí cất, tình trạng, ghi chú khôi phục…" />
            </label>
            {tagsBlock}
          </>
        ) : null}

        {/* ============ ADVANCED (chung) ============ */}
        <div className="border-t border-border/60 pt-2">
          <button type="button" onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            {showAdvanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Nâng cao (nhắc trước, section, trường tùy chỉnh{isLamDo ? ", sản phẩm tham chiếu" : ""})
          </button>
          {showAdvanced ? (
            <div className="mt-3 space-y-3">
              <label className="block text-sm">Nhắc trước (ngày)
                <Input type="number" min={0} max={60} value={reminderDays}
                  onChange={(e) => setReminderDays(e.target.value)} placeholder="Mặc định: 3" className="mt-1" />
              </label>
              {isLamDo ? (
                <label className="block text-sm">Sản phẩm tham chiếu
                  <div className="mt-1"><ProductMultiPicker value={productIds} onChange={setProductIds} /></div>
                </label>
              ) : null}
              {sections && sections.length > 0 ? (
                <label className="block text-sm">Section
                  <Select value={sectionId ?? ""} onChange={(e) => setSectionId(e.target.value || null)} className="mt-1">
                    <option value="">— Không thuộc section —</option>
                    {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Select>
                </label>
              ) : null}
              {fields && fields.length > 0 ? (
                <div className="space-y-2 rounded border border-border bg-muted/30 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Trường tùy chỉnh</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {fields.map((f) => {
                      const k = f.key || f.id;
                      const v = customValues[k];
                      const set = (val: unknown) => setCustomValues({ ...customValues, [k]: val });
                      if (f.type === "checkbox") {
                        return (
                          <label key={f.id} className="flex items-center gap-2 text-sm sm:col-span-2">
                            <input type="checkbox" checked={!!v} onChange={(e) => set(e.target.checked)} />
                            {f.name}
                          </label>
                        );
                      }
                      if (f.type === "select") {
                        return (
                          <label key={f.id} className="block text-sm">{f.name}
                            <Select value={(v as string) ?? ""} onChange={(e) => set(e.target.value)} className="mt-1">
                              <option value="">—</option>
                              {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </Select>
                          </label>
                        );
                      }
                      if (f.type === "text" || !f.type) {
                        return (
                          <label key={f.id} className="block text-sm">{f.name}
                            <div className="mt-1">
                              <SuggestInput value={(v as string) ?? ""} onChange={(val) => set(val)}
                                suggestions={getFieldHistory("custom:" + k)} />
                            </div>
                          </label>
                        );
                      }
                      return (
                        <label key={f.id} className="block text-sm">{f.name}
                          <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                            value={((v as string | number | undefined) ?? "") as string | number}
                            onChange={(e) => set(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                            className="mt-1" />
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {err ? <ErrorBox message={err} /> : null}
        <div className="flex justify-end gap-2">
          <GhostBtn type="button" onClick={onClose}>Hủy</GhostBtn>
          <PrimaryBtn type="submit" disabled={save.isPending}>{save.isPending ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
        </div>
      </form>
    </Modal>
  );
}

